/**
 * Sidebar — host-aware lewe menu (F7b T7b.7).
 *
 * **Admin scope:** statyczne menu z `config/menu.ts` (ADMIN_MENU). Bez zmian wobec F7a.
 * **Tenant scope:** dynamiczne menu z `/api/my/modules` — merge `catalog` + `subscriptions`:
 *   - moduł w subscriptions → `<NavLink>` aktywny (prowadzi do `/modules/<code>`; route'y
 *     modułów dojdą w F11/F12/F13+).
 *   - moduł tylko w catalog → disabled `<div>` z tooltipem „Moduł nieaktywny" + badge.
 * **Public scope:** Sidebar nie jest renderowany (public scope nie używa Layout) — ale
 *   na wszelki wypadek fallback do admin menu żeby coś się narysowało.
 *
 * Rendering w aplikacji: tenant Layout (T7b.8) wrap'uje Sidebar. W T7b.7 komponent jest
 * testowany w izolacji — nie wire'ujemy jeszcze tenant pages w Layout.
 */
import {
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
  ADMIN_SYSTEM_ITEMS,
  type AdminMenuItem,
  type AdminSystemItemDefinition,
  type AdminSystemItemKey,
  BASE_PACKAGE_KARTOTEKA_SLUGS,
  BASE_PACKAGE_KARTOTEKI,
  classifyMenuLinkUrl,
  DOCUMENT_KARTOTEKI,
  type DocumentKartotekaSlug,
  extractDocumentTypeIdsFromBaseFilters,
  findSystemKartotekaTarget,
  type HrAbsenceDocumentClass,
  type HrAbsenceDocumentTypeRef,
  type MenuItem,
  type MenuItemView,
  SYSTEM_ITEMS,
  type SystemItemKey,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  BadgeDollarSign,
  Boxes,
  Briefcase,
  Bug,
  Building2,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Contact,
  Database,
  FileStack,
  FileText,
  FolderLock,
  GitBranch,
  Landmark,
  LayoutDashboard,
  LayoutTemplate,
  Library,
  type LucideIcon,
  Map as MapIcon,
  MenuSquare,
  Package,
  Plane,
  Receipt,
  ScrollText,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Ticket,
  UserCog,
  Users,
  Warehouse,
  X,
} from 'lucide-react';

import workflow365Logo from '@/assets/workflow365-logo.svg';
import workflow365Mark from '@/assets/workflow365-mark.png';
import { Alert } from '@/components/Alert';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { AdminLayoutSwitcherFooter } from '@/components/menu/AdminLayoutSwitcherFooter';
import { ColumnResizer } from '@/components/menu/ColumnControls';
import { MenuModeSwitcher } from '@/components/menu/MenuModeSwitcher';
import { useTenantMenuViewSelection } from '@/components/menu/TenantMenuViewSwitcher';
import { resolvePickerIcon } from '@/components/modules/IconPicker';
import { Overlay } from '@/components/Overlay';
import { ProcessesPendingBadge } from '@/components/processes/ProcessesPendingBadge';
import {
  SIDEBAR_WIDTH,
  SidebarCollapseProvider,
  SidebarCollapseToggle,
  sidebarItemClass,
  SidebarItemLabel,
  tenantSidebarItemClass,
  useIsSidebarCollapsed,
  useSidebarCollapsedState,
  useSidebarWidth,
} from '@/components/sidebar-collapse';
import {
  adminItemColor,
  adminSectionColor,
  iconColorByName,
  SidebarIcon,
} from '@/components/sidebar-icon';
import { SidebarBrand } from '@/components/SidebarBrand';
import { CompanySwitcher } from '@/components/tenant/CompanySwitcher';
import { Tooltip } from '@/components/Tooltip';
import { ADMIN_MENU } from '@/config/menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useActiveAdminMenuLayout } from '@/hooks/admin-menu/useActiveAdminMenuLayout';
import { ACTIVE_MENU_LAYOUT_QUERY_KEY, useActiveMenuLayout } from '@/hooks/useActiveMenuLayout';
import { useHostScope } from '@/hooks/useHostScope';
import {
  useAreaMapQuery,
  useHrAbsenceDocumentTypeQuery,
  useInstalledModulesMenuQuery,
} from '@/hooks/useInstalledModulesMenuQuery';
import { useMenuMode } from '@/hooks/useMenuMode';
import { MENU_PREFERENCE_KEY, useAvailableMenuLayouts } from '@/hooks/useMenuPreference';
import { useRailPreferences } from '@/hooks/useRailPreferences';
import { useSidebarSkin } from '@/hooks/useSidebarSkin';
import { api } from '@/lib/api';
import { resolveLucideIcon } from '@/lib/lucide-all-icons';
import { MENU_COLOR_RGB, parseMenuColor } from '@/lib/menu-colors';
import { announceMenuItemActivated } from '@/lib/menu-item-activation';
import {
  isRailTileVisible,
  RAIL_LOCKED_TILE_KEYS,
  type RailPreferences,
  type RailTileKey,
  visibleRailItems,
} from '@/lib/rail-preferences';
import { type CatalogModule, type MyModulesResponse, useMyModules } from '@/lib/use-my-modules';
import { cn } from '@/lib/utils';
// Uwaga: mechanizm KOLORU per obszar (lib/sidebar-area-colors) został wycięty 2026-09-16 —
// menu jest jednolicie jasne, tło treści płaskie (decyzja Eugenii). Import usunięty świadomie.

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  Server,
  LayoutTemplate,
  Package,
  BadgeDollarSign,
  Database,
  Building2,
  Library,
  Receipt,
  FileStack,
  FileText,
  FolderLock,
  GitBranch,
  Landmark,
  Ticket,
  ShieldCheck,
  ScrollText,
  Bug,
  MenuSquare,
  Settings,
  UserCog,
  BadgeCheck,
  Contact,
  Warehouse,
  briefcase: Briefcase,
  Briefcase,
  users: Users,
  Users,
};

export function resolveIcon(
  name: string | null | undefined,
  fallback: LucideIcon = Package,
): LucideIcon {
  if (!name) return fallback;
  return ICON_MAP[name] ?? fallback;
}

/**
 * Ikona nagłówka sekcji menu — z PEŁNEJ biblioteki lucide (kreator menu wybiera ikonę sekcji z
 * pełnego zestawu), z mapą paska jako pierwszym źródłem. Brak nazwy / nieznana nazwa → bez ikony
 * (sekcja nie dostaje zastępczej ikony, w przeciwieństwie do liści).
 */
export function resolveSectionIcon(name: string | null | undefined): LucideIcon | null {
  if (!name) return null;
  return ICON_MAP[name] ?? resolveLucideIcon(name) ?? null;
}

/** Nagłówek sekcji menu (wszystkie warianty paska): opcjonalna ikona z kreatora + etykieta. */
function SidebarSectionHeader({ label, iconName }: { label: ReactNode; iconName?: string | null }) {
  const Icon = resolveSectionIcon(iconName);
  return (
    <div className="flex items-center gap-1.5 px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
      {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
      <span className="min-w-0 truncate">{label}</span>
    </div>
  );
}

export function Sidebar() {
  const scope = useHostScope();
  if (scope === 'tenant') return <TenantSidebar />;
  return <AdminSidebar />;
}

// ---------------------------------------------------------------------------
// Admin scope — custom layout z DB (ADR-0034) z fallback B.1 do statycznego
// ADMIN_MENU + filtra per-rola.
//
// Resolver `useActiveAdminMenuLayout()` zwraca:
//   - `{layout: {...hierarchy, ...}}` gdy admin ma preference/role assignment/default
//   - `{layout: null}` (200) gdy żaden z 3 poziomów nie matchuje → fallback B.1
//
// Fallback B.1 rendering: statyczny ADMIN_MENU z config/menu.ts z filtrem
// `requiredRoles?: AdminRole[]` (pominięcie pola = widoczne dla wszystkich, „Edytor
// menu" widoczny TYLKO dla super_admin) oraz `requiredPermission?: string` (kod
// uprawnienia admina, np. `db_servers.manage` dla „Serwery baz").
// ---------------------------------------------------------------------------
function AdminSidebar() {
  const { t } = useTranslation();
  const { admin, hasPermission } = useAuth();
  const { collapsed, toggle } = useSidebarCollapsedState();
  const { widthPx, setWidthPx } = useSidebarWidth();
  const skin = useSidebarSkin();
  const layoutQuery = useActiveAdminMenuLayout();

  const customHierarchy =
    (layoutQuery.data?.layout?.hierarchy as AdminMenuItem[] | undefined) ?? [];
  const useCustom = customHierarchy.length > 0;

  const sortedItems = [...ADMIN_MENU]
    .filter((i) => i.visible)
    .filter(
      (i) =>
        !i.requiredRoles ||
        i.requiredRoles.length === 0 ||
        (admin?.role && i.requiredRoles.includes(admin.role)),
    )
    .filter((i) => !i.requiredPermission || hasPermission(i.requiredPermission))
    .sort((a, b) => a.order - b.order);

  // Lupka nad menu — filtruje statyczne ADMIN_MENU po etykiecie na żywo. Dla custom
  // layoutu (useCustom) szukajki nie pokazujemy (hierarchia dynamiczna, inny renderer).
  const [menuQuery, setMenuQuery] = useState('');
  const menuQ = menuQuery.trim().toLowerCase();
  const visibleItems = menuQ
    ? sortedItems.filter((i) => i.label.toLowerCase().includes(menuQ))
    : sortedItems;

  return (
    <SidebarCollapseProvider collapsed={collapsed}>
      <aside
        className={`relative flex min-h-screen flex-col border-r border-border-secondary bg-bg-elevated ${
          collapsed ? 'w-14' : ''
        }`}
        style={collapsed ? undefined : { width: widthPx }}
        data-testid="admin-sidebar"
        data-skin={skin}
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        {/* Przełącznik zwinięcia — pastylka na prawej krawędzi panelu. */}
        <SidebarCollapseToggle collapsed={collapsed} onToggle={toggle} />
        <div
          className={`flex items-center border-b border-border-secondary py-5 ${
            collapsed ? 'justify-center px-2' : 'px-4'
          }`}
        >
          {!collapsed && <SidebarBrand title="SaaS v2" subtitle="Panel operatora" />}
        </div>

        {!collapsed && !useCustom && (
          <div className="border-b border-border-secondary px-2 py-3">
            <Input
              type="search"
              value={menuQuery}
              onChange={(e) => setMenuQuery(e.target.value)}
              placeholder={t('common.sidebar.searchPlaceholder')}
              aria-label={t('common.sidebar.searchPlaceholder')}
              prefixIcon={<Search className="h-4 w-4" aria-hidden="true" />}
              data-testid="admin-sidebar-search"
            />
          </div>
        )}

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-4">
          {useCustom ? (
            <AdminCustomLayoutNav hierarchy={customHierarchy} />
          ) : (
            visibleItems.map((item) => {
              const Icon = resolveIcon(item.icon, LayoutDashboard);
              const isComingSoon = item.status === 'coming_soon';

              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  title={
                    collapsed ? item.label : isComingSoon ? `Dostępne w: ${item.phase}` : undefined
                  }
                  className={({ isActive }) => sidebarItemClass(collapsed, isActive)}
                >
                  <SidebarIcon icon={Icon} color={adminItemColor(item.key)} />
                  <SidebarItemLabel>{item.label}</SidebarItemLabel>
                  {isComingSoon && !collapsed && (
                    <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      Wkrótce
                    </span>
                  )}
                </NavLink>
              );
            })
          )}
          {!useCustom && menuQ && visibleItems.length === 0 && (
            <p className="px-4 py-2 text-sm text-text-muted" data-testid="admin-sidebar-empty">
              {t('common.sidebar.searchNoResults')}
            </p>
          )}
        </nav>

        {!collapsed && <AdminLayoutSwitcherFooter />}

        {!collapsed && (
          <div className="border-t border-border-secondary px-4 py-3 text-xs text-text-muted">
            <div>Faza 2 MVP</div>
            <div className="mt-0.5">v0.1.0</div>
          </div>
        )}
      </aside>
      {!collapsed && (
        <ColumnResizer
          width={widthPx}
          onWidthChange={setWidthPx}
          min={SIDEBAR_WIDTH.min}
          max={SIDEBAR_WIDTH.max}
        />
      )}
    </SidebarCollapseProvider>
  );
}

// ---------------------------------------------------------------------------
// Tenant sidebar — model „przełącznik WIDOKU menu" (dolna zona, drop-up popover):
//   1. Górna zona — menu CAŁEGO zakupionego modułu wybranego w popoverze (wszystkie
//      top-level pozycje z `moduleCode` równym wybranemu, renderowane przez
//      InstalledMenuItemNode; pokazujemy WSZYSTKIE zaprojektowane pozycje — także te
//      wskazujące trasy bazy stałej, jeśli projektant je dodał).
//   2. Przełącznik widoku menu — `TenantMenuViewSwitcher` w stopce sidebara: PŁASKA
//      lista zakupionych modułów (bez grupowania po obszarze — obszar to grupowanie
//      po stronie admina, NIE poziom nawigacji na tenancie), zaznaczenie = checkmark +
//      podświetlenie. Domyślnie wybrany `crm_core`.
//   3. Baza stała (na dole) — Użytkownicy / Firmy / Ustawienia, zawsze widoczne.
//
// ADR-0031 (custom layout z /menu-builder) pozostaje BEZ zmian — gdy admin tenanta
// zapisał hierarchy, renderujemy ją w całości (CustomLayoutNav) zamiast tego modelu.
// ---------------------------------------------------------------------------
function TenantSidebar() {
  const { t } = useTranslation();
  const { collapsed, toggle } = useSidebarCollapsedState();
  const { widthPx, setWidthPx } = useSidebarWidth();
  const skin = useSidebarSkin();
  const { mode: menuMode } = useMenuMode();
  const layoutQuery = useActiveMenuLayout();
  const modulesQuery = useMyModules(); // zachowane dla CustomLayoutNav (lookup catalog/subscriptions)
  const installedQuery = useInstalledModulesMenuQuery();

  const customHierarchy = (layoutQuery.data?.layout?.hierarchy as MenuItem[] | undefined) ?? [];
  const useCustom = customHierarchy.length > 0;
  const activeCustomId = useCustom ? (layoutQuery.data?.layout?.id ?? null) : null;

  // Menu customowe z Kreatora dostępne userowi (kaskada uprawnień) — widoki paska obok modułów,
  // teraz w JEDNEJ scalonej liście (przestawialne i ukrywalne razem z modułami — decyzja Eugenii
  // 2026-10-01). Puste/nieaktywne menu odsiewa hook.
  const { customViews, isLoading: customLoading } = useCustomRailViews();
  const queryClient = useQueryClient();
  const toast = useToast();
  // layoutId = UUID → menu customowe; null = jawny widok modułowy (backend to wspiera).
  const setMenuViewMutation = useMutation({
    mutationFn: async (layoutId: string | null) => {
      await api.put('/auth/menu-preference', { layoutId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MENU_PREFERENCE_KEY });
      void queryClient.invalidateQueries({ queryKey: ACTIVE_MENU_LAYOUT_QUERY_KEY });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.sidebar.viewSwitcher.switchError') });
    },
  });

  const { areas, views: allViews, allowedMenu, defaultMenuCode } = useTenantMenuViews();
  // Osobiste ustawienia paska (kolejność + ukryte) — warstwa wyglądu nad menu z uprawnień.
  // Kolor ikony liczony z NATURALNEJ pozycji widoku, więc przestawienie nie zmienia barw.
  const { preferences: railPrefs, isReady: railReady } = useRailPreferences();
  // Scalona lista widoków paska: moduły (naturalnie pierwsze) + menu customowe. Osobiste
  // ustawienia (kolejność + ukryte) nakładane na CAŁOŚĆ, więc menu customowe da się wpleść między
  // moduły i ukryć tak samo jak moduł. Kolor po naturalnej pozycji → przestawienie barw nie zmienia.
  const combinedNatural = useMemo<CustomRailView[]>(
    () => [...allViews.map((v) => ({ ...v, colorVar: null })), ...customViews],
    [allViews, customViews],
  );
  // Dopóki układ nieznany (ładuje się `me`) albo menu customowe jeszcze nie przyszły — pusto,
  // żeby ukryte widoki nie mignęły w domyślnej kolejności (bezpiecznik „nigdy pusto" pokazałby
  // np. wszystkie moduły, gdy widoczne zostało tylko menu customowe).
  const railItems = useMemo(
    () => (railReady && !customLoading ? visibleRailItems(combinedNatural, railPrefs) : []),
    [combinedNatural, railPrefs, railReady, customLoading],
  );
  // Moduły do wyboru = moduły z TEJ SAMEJ listy co pasek (bez osobnego bezpiecznika) — panel listy
  // nigdy nie otwiera modułu, którego na pasku nie ma. Pusto = wszystkie moduły ukryte.
  const views = useMemo(
    () => railItems.filter((v) => !v.code.startsWith(CUSTOM_VIEW_PREFIX)),
    [railItems],
  );
  const railColorFor = useMemo(() => combinedRailColorLookup(combinedNatural), [combinedNatural]);
  // Domyślny widok po wejściu = moduł crm_core („Systemowe") — pakiet, który ma każdy
  // tenant. Kod widoku = kod modułu (per-moduł grouping), więc celujemy w `crm_core`.
  // Domyślny widok: menu oznaczone gwiazdką (defaultMenuCode) → inaczej pierwsze dostępne z góry
  // (reguła „brak gwiazdki = pierwsze od góry") → inaczej crm_core. Przy pełnym dostępie zostaje crm_core.
  const defaultViewCode = allowedMenu
    ? (defaultMenuCode ?? views[0]?.code ?? CRM_CORE_MODULE_CODE)
    : CRM_CORE_MODULE_CODE;
  const { selectedCode, selectedModule, selectModule } = useTenantMenuViewSelection(
    views,
    defaultViewCode,
  );
  // Klik ikony widoku: menu customowe (kod z prefiksem) → zapis preferencji; moduł → wybór modułu,
  // a jeśli aktywne było menu customowe, wracamy do widoku modułowego (preferencja = null).
  const handleSelectView = (code: string) => {
    if (code.startsWith(CUSTOM_VIEW_PREFIX)) {
      const id = code.slice(CUSTOM_VIEW_PREFIX.length);
      if (id !== activeCustomId) setMenuViewMutation.mutate(id);
      return;
    }
    selectModule(code);
    if (activeCustomId) setMenuViewMutation.mutate(null);
  };
  // Otwarty widok musi być na pasku. Dwa przypadki przełączenia na pierwszą widoczną ikonę:
  //  - aktywne menu customowe zostało ukryte w ustawieniach paska (parytet z modułem: ukrycie
  //    otwartego widoku przełącza na pierwszy widoczny);
  //  - wszystkie moduły ukryte, a żadne menu customowe nie jest aktywne — bez tego panel byłby
  //    pusty, choć pasek pokazuje menu customowe.
  // Czekamy na komplet danych (ustawienia paska, menu customowe, aktywny układ) i na koniec
  // poprzedniego przełączenia, żeby nie przełączać na niepełnym obrazie ani w pętli.
  const layoutSettled = !layoutQuery.isLoading && !layoutQuery.isFetching;
  // Tryby „drzewo" i „mapa dostępu" nie rysują paska widoków (patrz warunki renderu niżej) —
  // tam osobiste ustawienia paska nie mają co przełączać.
  const listRailShown = !(
    (menuMode === 'tree' || menuMode === 'columns') &&
    !collapsed &&
    !useCustom
  );
  // Ostatnio podjęte przełączenie — nieudany zapis preferencji nie wraca w pętli z tym samym celem.
  const autoSwitchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!listRailShown || !railReady || customLoading || !layoutSettled) return;
    if (setMenuViewMutation.isPending) return;
    const first = railItems[0];
    if (!first) return;
    const activeCustomCode = activeCustomId ? `${CUSTOM_VIEW_PREFIX}${activeCustomId}` : null;
    // Tylko skutki OSOBISTEGO ukrycia — aktywne menu spoza listy z innego powodu (np. niedostępne)
    // i tenant bez modułów zostają jak były.
    const activeHidden =
      activeCustomCode !== null &&
      railPrefs.hidden.includes(activeCustomCode) &&
      !railItems.some((v) => v.code === activeCustomCode);
    const noModuleToShow = activeCustomCode === null && allViews.length > 0 && views.length === 0;
    if (!activeHidden && !noModuleToShow) {
      autoSwitchRef.current = null;
      return;
    }
    const attempt = `${activeCustomCode ?? 'none'}->${first.code}`;
    if (autoSwitchRef.current === attempt) return;
    autoSwitchRef.current = attempt;
    handleSelectView(first.code);
    // handleSelectView celowo poza deps — nowa referencja co render, logika zależy od pól niżej.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listRailShown,
    railReady,
    customLoading,
    layoutSettled,
    setMenuViewMutation.isPending,
    railItems,
    railPrefs.hidden,
    allViews.length,
    views.length,
    activeCustomId,
  ]);
  const activeCustomName = activeCustomId
    ? (customViews.find((v) => v.code === `${CUSTOM_VIEW_PREFIX}${activeCustomId}`)?.name ??
      layoutQuery.data?.layout?.name ??
      null)
    : null;

  // Menu modułu pokazuje zaprojektowane pozycje, ale BEZ duplikatów bazy stałej
  // (decyzja 2026-07-21): pozycje wskazujące Użytkownicy/Firmy/Ustawienia żyją w zawsze
  // widocznej bazie na dole, więc odsiewamy je z górnego widoku (`stripPinnedBaseDuplicates`).
  // Potem przycinamy puste sekcje (nagłówek bez dzieci).
  // Decyzje (processes) NIE są już przypinane w stopce — w modelu „widok = moduł"
  // (2026-08-06) to osobny widok modułu (processes → „Procesy" z pozycją „Decyzje").
  // KSeF (ksef_integration) od 2026-09-09 jest kartoteką „Dokumenty KSeF" (system-leaf,
  // lista /ksef), więc renderuje się jak każda inna kartoteka, nie jako funkcja. Stąd górny
  // widok odsiewa już tylko duplikaty bazy stałej (Użytkownicy/Firmy/Ustawienia).
  const selectedItems: InstalledMenuItem[] = selectedModule
    ? pruneEmptySections(stripPinnedBaseDuplicates(selectedModule.items))
    : [];

  // KOLOR PER OBSZAR WYCIĘTY (2026-09-16): brak `--content-bg-image` / `--area-color` /
  // per-obszar skali akcentu. Tło treści płaskie (index.css), górny pasek akcentu = fallback
  // #00459a (TenantLayout), akcent = domyślna niebieska skala z index.css. Jednolicie.

  // Tryb nr 2 = wąski floating pasek ikon obszarów (zastępuje całą powłokę sidebara). Nie
  // dotyczy trybu zwiniętego ani własnego układu menu (custom layout renderujemy normalnie).
  if (menuMode === 'tree' && !collapsed && !useCustom) {
    return <TenantMenuRail areas={areas} />;
  }

  // Tryb nr 3 = „Mapa dostępu": slim pasek bez obszarów; najechanie wysuwa duży drawer z PEŁNYM
  // spisem wszystkich kartotek pogrupowanych po obszarach (kolor per obszar). Ten sam gating.
  if (menuMode === 'columns' && !collapsed && !useCustom) {
    return <TenantMenuAccessRail areas={areas} />;
  }

  return (
    <SidebarCollapseProvider collapsed={collapsed}>
      <div className="flex items-stretch" data-testid="tenant-sidebar-shell">
        {/* Pasek boczny „dostępne menu" (kolorowe ikony widoków) — ZAWSZE otwarty. Zwijanie
            dotyczy tylko panelu listy obok; pasek zostaje. Widoki = moduły + menu customowe z
            Kreatora (aktywne podświetlone; klik modułu wraca do widoku modułowego). */}
        <TenantListRail
          views={railItems}
          colorFor={railColorFor}
          railPrefs={railReady ? railPrefs : null}
          activeCode={activeCustomId ? `${CUSTOM_VIEW_PREFIX}${activeCustomId}` : selectedCode}
          onSelect={handleSelectView}
          collapsed={collapsed}
          onToggleCollapse={toggle}
        />

        {/* Panel listy — otwarty domyślnie, zwijalny ikoną na pasku bocznym (chowa cały panel,
            zostaje sam pasek z ikonami). Pływający, zaokrąglony (parytet z paskiem i treścią). */}
        {!collapsed && (
          <aside
            className="relative my-3 flex flex-col overflow-hidden rounded-r-2xl border border-border-secondary bg-bg-elevated shadow-lg"
            style={{ width: widthPx, height: 'calc(100vh - 1.5rem)' }}
            data-testid="tenant-sidebar"
            data-skin={skin}
            data-collapsed="false"
          >
            {/* Marka „Workflow365" (20px/28 semibold) + nazwa menu pod spodem (12px/18, niebieski).
                Dokładnie wg Figmy: pt-20, pl-12 (=px-4+pl-3), pb-14. */}
            <div className="flex flex-none flex-col gap-0.5 px-4 pb-[14px] pl-7 pt-5">
              <div className="text-[20px] font-semibold leading-[28px] tracking-[-0.3px] text-[#17171f] dark:text-text-primary">
                Workflow365
              </div>
              <div className="truncate text-[12px] font-bold leading-[18px] text-[#2463eb] dark:text-[#93b0ff]">
                {activeCustomName ?? selectedModule?.name ?? t('tenant.sidebar.brandTagline')}
              </div>
            </div>

            {/* Wybór firmy — h-44, rounded-28, bg #f1f1f4 (Figma). */}
            <div
              className="mt-[10px] flex h-11 flex-none items-center px-4"
              data-testid="tenant-sidebar-company"
            >
              <CompanySwitcher />
            </div>

            {/* Kartoteki pod nagłówkiem „GŁÓWNE" (Figma: pt-30, pl-12, tracking-0.6, #7b7b8b).
                Dashboard/Kalendarz/Mapa tylko jako ikony na pasku. */}
            <nav
              className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-2"
              aria-label={t('tenant.sidebar.navAria')}
            >
              {!useCustom && (
                <div className="pb-[6px] pl-3 pt-[30px] text-[12px] font-semibold uppercase leading-[16px] tracking-[0.6px] text-[#7b7b8b] dark:text-text-muted">
                  {t('tenant.sidebar.sectionMain')}
                </div>
              )}
              {installedQuery.isLoading && !useCustom && (
                <div
                  className="space-y-2 px-2 py-2"
                  data-testid="tenant-sidebar-loading"
                  role="status"
                  aria-label={t('tenant.sidebar.loading')}
                >
                  <div className="h-8 w-full animate-pulse rounded bg-bg-tertiary" />
                  <div className="h-8 w-full animate-pulse rounded bg-bg-tertiary" />
                  <div className="h-8 w-4/5 animate-pulse rounded bg-bg-tertiary" />
                </div>
              )}

              {installedQuery.isError && !useCustom && (
                <div
                  role="alert"
                  className="mx-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                >
                  {t('tenant.sidebar.error')}
                </div>
              )}

              {useCustom && modulesQuery.data ? (
                <CustomLayoutNav hierarchy={customHierarchy} modules={modulesQuery.data} />
              ) : null}

              {!useCustom &&
                (selectedCode === HR_VIEW_CODE ? (
                  <HrQuickNav areas={areas} allowedMenu={allowedMenu} />
                ) : (
                  <div className="space-y-0.5" data-testid="tenant-sidebar-module-menu">
                    {selectedItems.map((item, i) => (
                      <InstalledMenuItemNode key={`${item.kind}:${item.slug}:${i}`} item={item} />
                    ))}
                  </div>
                ))}

              {!useCustom &&
                views.length === 0 &&
                !installedQuery.isLoading &&
                !installedQuery.isError && (
                  <div
                    className="px-4 py-2 text-sm text-text-muted"
                    data-testid="tenant-sidebar-empty"
                  >
                    {t('tenant.sidebar.empty')}
                  </div>
                )}

              {/* Baza — Użytkownicy / Firmy / Ustawienia INLINE pod modułami (jak Figma, bez
                  nagłówka „Inne ustawienia"). */}
              {!useCustom && (
                <div className="mt-3 space-y-0.5" data-testid="tenant-sidebar-pinned-base">
                  {SYSTEM_ITEMS.filter((s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey)).map(
                    (def) => {
                      const Icon = resolveIcon(def.iconName, Package);
                      const label = t(def.labelI18nKey, def.defaultLabel);
                      return (
                        <NavLink
                          key={def.itemKey}
                          to={def.path}
                          data-testid={`sidebar-link-${def.itemKey}`}
                          className={({ isActive }) => tenantSidebarItemClass(false, isActive)}
                        >
                          <SidebarIcon
                            icon={Icon}
                            color={iconColorByName(def.iconName)}
                            sizeClass="h-5 w-5"
                          />
                          <SidebarItemLabel>{label}</SidebarItemLabel>
                        </NavLink>
                      );
                    },
                  )}
                </div>
              )}
            </nav>
          </aside>
        )}
        {!collapsed && (
          <ColumnResizer
            width={widthPx}
            onWidthChange={setWidthPx}
            min={SIDEBAR_WIDTH.min}
            max={SIDEBAR_WIDTH.max}
          />
        )}
      </div>
    </SidebarCollapseProvider>
  );
}

/**
 * Special-case routing per moduł — landing path inny niż generic
 * `/modules/<code>`. F17 T17.1.6 v3: moduł `processes` landuje na
 * `/processes/decisions/pending` (zakładka „Moje czekające").
 * Patrz ADR-0040.
 */
const MODULE_LANDING_OVERRIDES: Record<string, string> = {
  processes: '/processes/decisions/pending',
  // KSeF — moduł integracji e-faktur prowadzi do listy faktur odebranych
  ksef_integration: '/ksef',
  // Document class modules (F20 T20.18)
  doc_contracts: '/contracts',
  doc_letters: '/letters',
  doc_commercial: '/commercial-docs',
  doc_internal: '/internal-docs',
  doc_specjalne: '/special-docs',
  // Kartoteki dokumentów (dedykowane trasy, nie /modules/<slug>) — używane gdy fragment
  // menu modułu dokumentowego rozwiązuje pozycję-moduł na liść systemowy po
  // reference_system_module_slug (np. doc_invoices_faktury_sprzedazy → sale_invoices).
  purchase_invoices: '/purchase-invoices',
  sale_invoices: '/sale-invoices',
  contracts: '/contracts',
  letters: '/letters',
  commercial: '/commercial-docs',
  internal: '/internal-docs',
  specjalne: '/special-docs',
  // Encje systemowe CRM (dedykowane trasy, nie /modules/<slug>) — używane gdy fragment
  // menu modułu systemowego rozwiązuje pozycję-moduł na liść systemowy po
  // reference_system_module_slug (np. crm_core_kontrahenci → contractors).
  contractors: '/contractors',
  employees: '/employees',
  users: '/users',
  companies: '/companies',
  contractor_contacts: '/contractor-contacts',
  tasks: '/tasks',
  // Moduł HR Kreatora → dedykowany ekran Kadr (jeden widok na obszar; bez
  // dublowania generyczną listą modułu). Patrz pages/ScheduleGrid.tsx.
  grafiki_zmiany: '/grafik',
};

/**
 * Kartoteki dokumentów po slugu — pakiet BAZOWY (crm_core), nie moduł (ADR-0083).
 * Fallback etykiety/ikony/trasy gdy tenant doda kartotekę dokumentu do własnego menu:
 * po wycofaniu modułów doc_* nie ma jej już w liściach zainstalowanych obszarów, więc
 * `installedLeafBySlug` jej nie zwróci — bierzemy metadane stąd.
 */
const BASE_KARTOTEKA_BY_SLUG = new Map<string, (typeof BASE_PACKAGE_KARTOTEKI)[number]>(
  BASE_PACKAGE_KARTOTEKI.map((d) => [d.slug, d]),
);

/**
 * Dokleja zawężenie do typów dokumentu jako kanoniczny `?documentTypeIds=<csv>` (lista
 * dokumentów parsuje CSV; liczba pojedyncza `documentTypeId` zostaje tylko dla wstecznej
 * zgodności starych linków). JEDYNE miejsce składające ten parametr — wołane przez
 * `installedLeafPath` (zainstalowane obszary), przez tryb drzewa (pozycje własnych menu,
 * `withMenuItemDocumentTypes`) i przez skrót „Nieobecności" (`resolveAbsencesHref`). Pusty zbiór
 * → ścieżka bez zmian.
 */
function withDocumentTypeIds(path: string, ids: readonly string[]): string {
  const uniqueIds = [...new Set(ids)];
  if (uniqueIds.length === 0) return path;
  return `${path}${path.includes('?') ? '&' : '?'}documentTypeIds=${encodeURIComponent(uniqueIds.join(','))}`;
}

/**
 * Trasa pozycji `module` własnego menu tenanta z zawężeniem z JEJ WŁASNEGO filtra stałego
 * (`view.baseFilters` → `document_type_id in […]`). Tryb drzewa rozwiązuje pozycje menu do
 * liści po samej ścieżce — bez tego zawężenie (np. „Wnioski urlopowe" → /special-docs tylko dla
 * typu WU) by przepadło. Nie używamy tu `?mi=`: `?mi=` odszukuje pozycję wyłącznie w AKTYWNYM
 * układzie własnym, a tryb drzewa rysuje się tylko wtedy, gdy takiego układu nie ma
 * (`!useCustom` w `TenantSidebar`) — pozycje galerii „Customowe" nie są w nim zawarte.
 */
function withMenuItemDocumentTypes(basePath: string, item: MenuItem): string {
  return withDocumentTypeIds(
    basePath,
    extractDocumentTypeIdsFromBaseFilters(item.view?.baseFilters),
  );
}

/**
 * Czy pozycja własnego układu ma przypisany widok (layout listy/formularza albo filtry/sortowanie
 * stałe) — wtedy jej link niesie `?mi=<id>`, żeby strona klienta honorowała to przypisanie
 * (patrz `RenderMenuItem` i `useActiveMenuItem`).
 */
function menuItemHasViewAssignment(item: MenuItem): boolean {
  return (
    !!item.viewRef ||
    !!item.listRef ||
    (item.view?.baseFilters?.length ?? 0) > 0 ||
    (item.view?.baseSort?.length ?? 0) > 0
  );
}

/**
 * Ścieżka pozycji własnego układu tak, jak ją linkuje `RenderMenuItem` (pozycja `module` z
 * przypisanym widokiem dostaje `?mi=`) — klucz do map ikon/etykiet zakładek, żeby dwie pozycje
 * na tę samą trasę (różniące się zawężeniem w swoim widoku) miały osobne wpisy.
 */
function customItemTabPath(item: MenuItem, route: string): string {
  return item.type === 'module' && menuItemHasViewAssignment(item)
    ? `${route}${route.includes('?') ? '&' : '?'}mi=${encodeURIComponent(item.id)}`
    : route;
}

/**
 * Ścieżka docelowa liścia menu modułu (routing NavLink). Zawężenie do typów dokumentu
 * (legacy `documentTypeId` z encji `document_class` + `documentTypeIds` z filtra stałego
 * `view.baseFilters`) łączymy w jeden zbiór i emitujemy jako `?documentTypeIds=<csv>` —
 * serwer akceptuje ten param (parseDocumentTypeIds), niezależnie skąd zawężenie przyszło.
 */
function installedLeafPath(item: InstalledMenuItem): string | null {
  if (item.kind === 'system') {
    const base = MODULE_LANDING_OVERRIDES[item.slug] ?? `/modules/${item.slug}`;
    return withDocumentTypeIds(base, [
      ...(item.documentTypeIds ?? []),
      ...(item.documentTypeId ? [item.documentTypeId] : []),
    ]);
  }
  if (item.kind === 'template')
    return MODULE_LANDING_OVERRIDES[item.slug] ?? `/modules/${item.slug}`;
  if (item.kind === 'link' && item.url && classifyMenuLinkUrl(item.url) === 'relative')
    return item.url;
  return null;
}

/**
 * Ścieżka pozycji zainstalowanego obszaru tak, jak linkuje ją `InstalledMenuItemNode`:
 * `installedLeafPath` plus `?mi=<menuItemId>` dla pozycji z przypisanym widokiem (moduł
 * systemowy / rodzaj danych). Klucz do map ikon/etykiet zakładek — zawężenie i `mi` wchodzą do
 * tożsamości (`menuIdentityKey`), więc mapa musi liczyć ścieżkę tą samą formułą co link
 * (bliźniak `customItemTabPath` dla własnych układów). Custom link (`kind: 'link'`) renderuje
 * się pod swoim `url` bez `?mi=`, więc tu też go nie dostaje.
 */
function installedItemPath(item: InstalledMenuItem): string | null {
  const basePath = installedLeafPath(item);
  if (!basePath || basePath === '#') return basePath;
  if (!item.menuItemId || item.kind === 'link') return basePath;
  return `${basePath}${basePath.includes('?') ? '&' : '?'}mi=${encodeURIComponent(item.menuItemId)}`;
}

/**
 * Przycina puste sekcje z menu modułu (rekurencyjnie) — sekcja bez widocznych dzieci
 * nie renderuje samego nagłówka. Odsiewanie duplikatów bazy stałej robi osobno
 * `stripPinnedBaseDuplicates` (wołane przed tym helperem).
 */
export function pruneEmptySections(items: InstalledMenuItem[]): InstalledMenuItem[] {
  const out: InstalledMenuItem[] = [];
  for (const item of items) {
    if (item.kind === 'section') {
      const children = pruneEmptySections(item.children ?? []);
      if (children.length === 0) continue;
      out.push({ ...item, children });
      continue;
    }
    out.push(item);
  }
  return out;
}

/**
 * Usuwa z menu modułu liście wskazujące na trasy bazy stałej (Użytkownicy/Firmy/
 * Ustawienia) — te pozycje żyją w ZAWSZE widocznej stopce na dole (decyzja 2026-07-21),
 * więc powielanie ich w górnym widoku modułu jest zbędne. Rekurencyjnie wchodzi w sekcje;
 * puste sekcje po odsianiu czyści `pruneEmptySections`. (Decyzje/KSeF NIE są już
 * odsiewane — w modelu „widok = moduł" to osobne widoki modułów, 2026-08-06.)
 */
export function stripPinnedBaseDuplicates(items: InstalledMenuItem[]): InstalledMenuItem[] {
  const out: InstalledMenuItem[] = [];
  for (const item of items) {
    if (item.kind === 'section') {
      out.push({
        ...item,
        children: stripPinnedBaseDuplicates(item.children ?? []),
      });
      continue;
    }
    const path = installedLeafPath(item);
    // Ustawienia wstawione świadomie do menu modułu (kreator ITE) zostają — to nie duplikat
    // stopki, tylko decyzja projektanta modułu (2026-10-01).
    if (path && PINNED_BASE_PATHS.has(path) && !item.systemItem) continue;
    out.push(item);
  }
  return out;
}

/**
 * Zona 3 — baza stała (Faza 1 „no core"): dokładnie 3 pozycje, ZAWSZE widoczne
 * niezależnie od tego jaki moduł jest wybrany w przełączniku. Metadata (ikona, i18n
 * key, path) z `SYSTEM_ITEMS` (@saas/shared) — jedno źródło prawdy z paletką
 * kreatora menu. Eksport: makieta podglądu menu w Kreatorze (`MenuPreviewSidebar`)
 * odwzorowuje tę samą bazę stałą.
 */
export const PINNED_BASE_ITEM_KEYS: readonly SystemItemKey[] = ['users', 'companies', 'settings'];

/**
 * Trasy bazy stałej — do deduplikacji z menu modułu (górny widok nie powiela pozycji,
 * które i tak są w zawsze widocznej bazie na dole). Liczone z `SYSTEM_ITEMS` (jedno
 * źródło prawdy ścieżek), żeby nie rozjechać się z `PinnedBaseNav`.
 */
const PINNED_BASE_PATHS = new Set(
  SYSTEM_ITEMS.filter((s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey)).map((s) => s.path),
);

/**
 * Trasy bazy platformowej odsiewane z kolumn obszarów w „Mapie dostępu" — baza żyje w
 * osobnej neutralnej kolumnie „Konto" (Dashboard + Użytkownicy/Firmy/Ustawienia), więc nie
 * dublujemy jej w kolumnach modułowych. `PINNED_BASE_PATHS` (users/companies/settings) + Dashboard.
 */
const ACCESS_MAP_BASE_PATHS = new Set<string>([...PINNED_BASE_PATHS, '/dashboard']);

/** Kod obszaru pakietu bazowego (seed katalogu ADM) — kolumna „Systemowe" na mapie obszarów. */
const SYSTEM_AREA_CODE = 'systemowe';
/** Kod syntetycznej pod-grupy „Dokumenty" (7 klas) w kolumnie „Systemowe" mapy obszarów. */
const ACCESS_MAP_DOCUMENTS_GROUP = '__documents__';
/** Slug klasy dokumentu → klucz `tenant.tabs.titles.*` (tłumaczona etykieta na mapie obszarów). */
const DOCUMENT_TAB_TITLE_KEYS: Record<DocumentKartotekaSlug, string> = {
  purchase_invoices: 'purchaseInvoices',
  sale_invoices: 'saleInvoices',
  contracts: 'contracts',
  letters: 'letters',
  commercial: 'commercialDocs',
  internal: 'internalDocs',
  specjalne: 'specialDocs',
};

/** Nawigowalna pozycja kolumny mapy obszarów (trasa + etykieta). */
interface AccessMapLeaf {
  to: string;
  label: string;
  /** Moduł niewykupiony — pozycja szara, bez linku, z plakietką. */
  inactive?: boolean;
}

/** Pod-grupa kolumny mapy obszarów (moduł albo syntetyczna grupa „Dokumenty"). */
interface AccessMapModule {
  code: string;
  name: string;
  leaves: AccessMapLeaf[];
}

/**
 * Widok „Kadry" — skróty do dedykowanych ekranów HR. NIE jest stałym blokiem:
 * renderuje się w górnej strefie sidebara tylko gdy w przełączniku wybrany jest widok
 * `HR_VIEW_CODE` (dostępny gdy firma ma co najmniej jeden moduł z obszaru `hr`, a osoba ma
 * w menu choć jeden z nich — patrz `hasHrArea` w `useTenantMenuViews`). Wewnątrz skrót oparty
 * na module HR przechodzi dwie bramki: (1) moduł jest zainstalowany (obecność `moduleCode` w
 * obszarze `hr` z `/my/installed-modules`) ORAZ (2) osoba ma ten moduł w menu
 * (`me.menuModules.allowed`; `null` = pełen dostęp — ta sama reguła co `moduleGroups`, patrz
 * `menuModuleAllowedPredicate`). Menu chowa tylko linki; dostęp do danych rozstrzygają
 * uprawnienia. Który moduł stoi za skrótem:
 *  - Grafik → `grafiki`, Teczka → `teczka` (kod zapisany wprost w `requiresModule`);
 *  - Nieobecności → idzie za OBOWIĄZUJĄCYM u klienta typem wniosku o nieobecność
 *    (`hrAbsenceDocumentType` z `/my/installed-modules` — ustawienie klienta, domyślnie typ
 *    oznaczony przez ITE): typ oznaczony przez ITE (także wybrany jawnie) → WYŁĄCZNIE pozycja
 *    modułu-WŁAŚCICIELA liścia menu z tym typem (dziś `urlopy`, kodu modułu nie zaszywamy), bez
 *    takiej pozycji brak skrótu; inny typ wybrany przez klienta → lista klasy zawężona do typu —
 *    patrz `resolveAbsencesHref`;
 *  - pulpit / Pracownicy / Struktura nie są związane z modułem HR (oparte na CRM Core) — bez
 *    bramki modułu, w tym widoku zawsze (Pracownicy dostępni też z widoku CRM).
 *
 * Etykiety przez i18n (namespace `hr.nav.*`); `label` to klucz tłumaczenia.
 */
const HR_AREA_CODE = 'hr';

/**
 * Kod modułu pakietu bazowego (crm_core) — domyślnie wybrany widok w przełączniku,
 * bo ten moduł ma każdy tenant. W modelu „widok = moduł" kod widoku = kod modułu.
 */
const CRM_CORE_MODULE_CODE = 'crm_core';

/** Kod syntetycznego widoku „Kadry" w przełączniku (nie moduł — skróty HR). */
const HR_VIEW_CODE = 'hr_quicknav';

const HR_QUICK_LINKS: ReadonlyArray<{
  /** Trasa statyczna. Pomiń dla pozycji z `resolveHref` (trasa zależna od instalacji). */
  to?: string;
  /**
   * Marker pozycji, której trasa NIE jest statyczna — Teczka (dokumenty pracownicze) wskazuje
   * kartotekę dokumentów zawężoną do `documentTypeId` konkretnej instalacji (UUID per tenant),
   * więc href liczy `resolveHrDocClassHref` z drzewa obszaru `hr` zamiast być zapisany wprost.
   * Wartość = kod modułu HR, którego liścia document_class szukamy.
   */
  resolveHref?: string;
  /**
   * Nieobecności: href liczony z obowiązującego typu „wniosek o nieobecność"
   * (`hrAbsenceDocumentType` z `/my/installed-modules`), a NIE z zaszytego kodu modułu —
   * reguła w `resolveAbsencesHref`.
   */
  resolveByAbsenceType?: boolean;
  /** Stabilny klucz do React key + `data-testid` (gdy brak `resolveHref`/statycznego `to`). */
  navKey?: string;
  /** Klucz i18n etykiety (namespace `hr.nav.*`). */
  label: string;
  Icon: LucideIcon;
  /**
   * Kod modułu HR wymagany, by link był widoczny: moduł zainstalowany ORAZ dopuszczony w menu
   * osoby. Brak = bez bramki modułu (pulpit/Pracownicy/Struktura — oparte na CRM Core).
   * Nieobecności go nie ustawiają — ich moduł wynika z liścia menu (`resolveByAbsenceType`).
   */
  requiresModule?: string;
}> = [
  { to: '/hr', label: 'hr.nav.dashboard', Icon: LayoutDashboard },
  {
    // Nieobecności — lista dokumentów zawężona do typu „wniosek o nieobecność". Który to typ,
    // ustala ustawienie klienta (domyślnie oznaczenie ITE), NIE zaszyty kod modułu/typu: href
    // liczymy z `hrAbsenceDocumentType` z `/my/installed-modules`.
    resolveByAbsenceType: true,
    navKey: 'absences',
    label: 'hr.nav.absences',
    Icon: Plane,
  },
  { to: '/grafik', label: 'hr.nav.schedule', Icon: Calendar, requiresModule: 'grafiki' },
  {
    resolveHref: 'teczka',
    label: 'hr.nav.employeeFile',
    Icon: FolderLock,
    requiresModule: 'teczka',
  },
  { to: '/employees', label: 'hr.nav.employees', Icon: BadgeCheck },
  { to: '/org-chart', label: 'hr.nav.structure', Icon: Building2 },
];

/**
 * Reguła widoczności modułu w menu osoby (`me.menuModules.allowed`): `null` = pełen dostęp
 * (super-user / brak nadpisania) → każdy moduł dopuszczony; inaczej moduł musi być na liście, a
 * pozycja bez kodu modułu (brak właściciela) NIE jest dopuszczona. Jedno źródło reguły dla
 * `moduleGroups`, `hasHrArea` (`useTenantMenuViews`) i skrótów `HrQuickNav`.
 */
function menuModuleAllowedPredicate(
  allowedMenu: readonly string[] | null,
): (moduleCode: string | undefined) => boolean {
  if (!allowedMenu) return () => true;
  const allow = new Set(allowedMenu);
  return (moduleCode) => moduleCode != null && allow.has(moduleCode);
}

/**
 * Rozwiązuje href liścia `document_class` (Teczka → dokumenty pracownicze/DOKPR) należącego
 * do modułu `moduleCode`, przeszukując rekurencyjnie drzewo pozycji obszaru HR (sekcje
 * fragmentu menu modułu mogą zagnieżdżać liść). `moduleCode` jest stemplowany TYLKO na
 * węzłach TOP-LEVEL fragmentu (patrz `InstalledMenuItem.moduleCode` w installed-menu.ts) —
 * zagnieżdżone dzieci dziedziczą przynależność po najbliższym przodku, więc niesiemy ją
 * w rekurencji zamiast wymagać własnego stempla na liściu.
 */
function resolveHrDocClassHref(
  items: InstalledMenuItem[],
  moduleCode: string,
  inheritedModuleCode?: string,
): string | null {
  for (const item of items) {
    const owner = item.moduleCode ?? inheritedModuleCode;
    if (item.kind === 'section') {
      const found = resolveHrDocClassHref(item.children ?? [], moduleCode, owner);
      if (found) return found;
      continue;
    }
    if (
      item.kind === 'system' &&
      owner === moduleCode &&
      (item.documentTypeId || (item.documentTypeIds?.length ?? 0) > 0)
    ) {
      // Ta sama ścieżka co link pozycji menu (z `?mi=`) — skrót otwiera ten sam widok i tę samą
      // zakładkę (nazwa/ikona z map kluczowanych `installedItemPath`).
      return installedItemPath(item);
    }
  }
  return null;
}

/**
 * Wariant `resolveHrDocClassHref` dopasowujący liść po GLOBALNYM `documentTypeId` (a nie po kodzie
 * modułu) — dla skrótu „Nieobecności", gdy obowiązuje typ domyślny ITE (`resolveAbsencesHref`).
 * Liść menu strukturalnie niesie globalne id typu z szablonu, więc dopasowanie po nim jest stabilne.
 * Element może dziś nieść zawężenie na POZYCJI MENU (`documentTypeIds`, liczba mnoga, ADR-0104b D4)
 * zamiast pojedynczego `documentTypeId` — sprawdzamy oba pola.
 *
 * Kod modułu, do którego skrót „Nieobecności" należy, wynika z samego liścia (jego właściciel —
 * `moduleCode` stemplowany na węzłach TOP-LEVEL, dzieci dziedziczą po przodku; tak samo jak w
 * `resolveHrDocClassHref`). Liście modułów niedopuszczonych w menu osoby (`isModuleAllowed`)
 * pomijamy i szukamy dalej — inny dopuszczony moduł może dostarczać ten sam typ.
 */
function resolveHrDocClassHrefByTypeId(
  items: InstalledMenuItem[],
  documentTypeId: string,
  isModuleAllowed: (moduleCode: string | undefined) => boolean,
  inheritedModuleCode?: string,
): string | null {
  for (const item of items) {
    const owner = item.moduleCode ?? inheritedModuleCode;
    if (item.kind === 'section') {
      const found = resolveHrDocClassHrefByTypeId(
        item.children ?? [],
        documentTypeId,
        isModuleAllowed,
        owner,
      );
      if (found) return found;
      continue;
    }
    if (
      item.kind === 'system' &&
      isModuleAllowed(owner) &&
      (item.documentTypeId === documentTypeId ||
        (item.documentTypeIds ?? []).includes(documentTypeId))
    ) {
      return installedItemPath(item);
    }
  }
  return null;
}

/**
 * Lista dokumentów klasy, na którą prowadzi skrót „Nieobecności" poza pozycją modułu — wyprowadzona
 * z mapy tras klas (`MODULE_LANDING_OVERRIDES`, klucz = slug kartoteki klasy), żeby trasa klasy
 * miała jedno źródło.
 */
const HR_ABSENCE_CLASS_LIST_PATH: Record<HrAbsenceDocumentClass, string> = {
  specjalne: requireLandingPath('specjalne'),
  internal: requireLandingPath('internal'),
};

function requireLandingPath(slug: string): string {
  const path = MODULE_LANDING_OVERRIDES[slug];
  if (!path) {
    throw new Error(`[Sidebar] brak trasy kartoteki klasy „${slug}" w MODULE_LANDING_OVERRIDES`);
  }
  return path;
}

/**
 * Trasa skrótu „Nieobecności" z OBOWIĄZUJĄCEGO typu wniosku o nieobecność (`absenceType` z
 * `/my/installed-modules`; `null`/`undefined` = nie wiadomo albo dane się ładują → brak skrótu):
 *  1. obowiązuje typ oznaczony przez ITE (`isGlobalDefault` — także gdy klient wybrał go jawnie)
 *     → WYŁĄCZNIE pozycja modułu w menu instalacji
 *     (zainstalowany moduł + moduł dopuszczony w menu osoby — `resolveHrDocClassHrefByTypeId`;
 *     niesie jego kontekst, np. zawężenie z pozycji menu); bez takiej pozycji brak skrótu — bez
 *     zapasowego linku, żeby skrót nie omijał menu ustawionego osobie przez administratora;
 *  2. typ wybrany przez klienta (spoza menu modułu) → lista klasy zawężona do typu
 *     (`?documentTypeIds=<id>`), o ile osoba ma prawo przeglądania tej klasy
 *     (`documents.<klasa>.view`) albo jest super-userem; inaczej brak skrótu (menu chowa link,
 *     dostęp i tak rozstrzyga serwer).
 */
function resolveAbsencesHref(params: {
  absenceType: HrAbsenceDocumentTypeRef | null | undefined;
  hrItems: InstalledMenuItem[];
  isModuleAllowed: (moduleCode: string | undefined) => boolean;
  canViewClass: (documentClass: HrAbsenceDocumentClass) => boolean;
}): string | null {
  const { absenceType, hrItems, isModuleAllowed, canViewClass } = params;
  if (!absenceType) return null;
  if (absenceType.isGlobalDefault) {
    return resolveHrDocClassHrefByTypeId(hrItems, absenceType.id, isModuleAllowed);
  }
  if (!canViewClass(absenceType.documentClass)) return null;
  return withDocumentTypeIds(HR_ABSENCE_CLASS_LIST_PATH[absenceType.documentClass], [
    absenceType.id,
  ]);
}

/**
 * Skróty widoku „Kadry". `allowedMenu` = `me.menuModules.allowed` (`null` = pełen dostęp): skrót
 * oparty na module HR znika, gdy osoba nie ma tego modułu w menu — nawet jeśli moduł jest
 * zainstalowany, a inne moduły HR osoba widzi (por. `HR_QUICK_LINKS`, `moduleGroups`).
 */
function HrQuickNav({
  areas,
  allowedMenu,
}: {
  areas: InstalledMenuArea[];
  allowedMenu: readonly string[] | null;
}) {
  const { t } = useTranslation();
  const collapsed = useIsSidebarCollapsed();
  const { me } = useTenantAuth();
  const isModuleAllowed = useMemo(() => menuModuleAllowedPredicate(allowedMenu), [allowedMenu]);
  // Obowiązujący typ „wniosek o nieobecność" (skrót „Nieobecności") — z tego samego endpointu
  // co `areas`, przez współdzielony queryKey (jeden fetch). `null`/`undefined` = nie wiadomo.
  const { data: hrAbsenceType } = useHrAbsenceDocumentTypeQuery();
  // Prawo przeglądania listy klasy — bramka skrótu prowadzącego na listę (nie na pozycję modułu).
  const canViewClass = (documentClass: HrAbsenceDocumentClass): boolean =>
    me?.user.isSuperUser === true ||
    (me?.permissions.includes(`documents.${documentClass}.view`) ?? false);
  // Kody modułów HR, które tenant faktycznie ma zainstalowane (z aktywną subskrypcją).
  const installedHrModules = useMemo(() => {
    const codes = new Set<string>();
    for (const area of areas) {
      if (area.code !== HR_AREA_CODE) continue;
      for (const item of area.items) if (item.moduleCode) codes.add(item.moduleCode);
    }
    return codes;
  }, [areas]);

  // Pozycje drzewa obszaru HR — bazowa lista, po której `resolveHrDocClassHref` szuka
  // liścia `document_class` dowolnej pozycji z `resolveHref` (nie tylko Teczki). Liczone
  // raz, niezależnie od tego, ile pozycji w HR_QUICK_LINKS ma ustawiony `resolveHref`.
  const hrItems = useMemo(() => {
    const hrArea = areas.find((a) => a.code === HR_AREA_CODE);
    return hrArea?.items ?? [];
  }, [areas]);

  // Bramka modułu: zainstalowany ORAZ dopuszczony w menu osoby. Skróty bez `requiresModule`
  // (pulpit/Pracownicy/Struktura) jej nie podlegają; bramkę Nieobecności robi resolver po
  // właścicielu liścia (moduł wynika z danych, nie z zaszytego kodu).
  const links = HR_QUICK_LINKS.filter(
    (link) =>
      !link.requiresModule ||
      (installedHrModules.has(link.requiresModule) && isModuleAllowed(link.requiresModule)),
  )
    .map((link) => ({
      ...link,
      to: link.resolveByAbsenceType
        ? resolveAbsencesHref({
            absenceType: hrAbsenceType,
            hrItems,
            isModuleAllowed,
            canViewClass,
          })
        : link.resolveHref
          ? resolveHrDocClassHref(hrItems, link.resolveHref)
          : link.to,
    }))
    // Moduł zainstalowany, ale href nierozwiązany (np. dane obszaru jeszcze się ładują,
    // albo fragment menu modułu nie niesie oczekiwanego liścia document_class) → nie
    // renderuj martwego linku zamiast NavLink bez trasy.
    .filter((link): link is typeof link & { to: string } => Boolean(link.to));

  return (
    <div className="space-y-0.5" data-testid="tenant-sidebar-hr">
      {!collapsed && (
        <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('hr.nav.section')}
        </p>
      )}
      {links.map(({ to, label, Icon, resolveHref, navKey }) => {
        const testIdKey = navKey ?? resolveHref ?? to.replace(/\//g, '');
        return (
          <NavLink
            key={navKey ?? resolveHref ?? to}
            to={to}
            data-testid={`sidebar-hr-${testIdKey}`}
            title={collapsed ? t(label) : undefined}
            className={({ isActive }) => tenantSidebarItemClass(collapsed, isActive)}
          >
            <SidebarIcon icon={Icon} color="#3b82f6" sizeClass="h-5 w-5" />
            <SidebarItemLabel>{t(label)}</SidebarItemLabel>
          </NavLink>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// F12.A T12.A10 — sekcja „Moduły własne" (custom modules tenanta).
// Pobiera GET /api/modules?show_in_menu=true. Brak modułów / błąd → silent (nie renderuj).
// ---------------------------------------------------------------------------

interface CustomModuleSidebarItem {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  /** Pochodzenie: NULL = moduł zbudowany ręcznie przez klienta; wartość = z szablonu. */
  sourceTemplateModuleId: string | null;
}

/**
 * Lista modułów custom do menu (GET /api/modules?show_in_menu=true). Wspólne
 * zapytanie sekcji „Moduły własne" (fallback) i renderera własnego układu
 * (hub Krok 4 — element wskazujący slug custom musi być aktywnym linkiem).
 */
function useCustomModulesForMenu() {
  return useQuery({
    queryKey: ['modules', 'menu'],
    queryFn: async () => {
      const res = await api.get<{ modules: CustomModuleSidebarItem[] }>(
        '/modules?show_in_menu=true',
      );
      return res.data.modules;
    },
    staleTime: 2 * 60 * 1000, // 2 min — zmiana modułu rzadsza niż refresh sidebara
  });
}

/** Custom menu tenanta (Kreator) z pełną hierarchią — zasila kafelek „Customowe" paska obszarów. */
interface MenuLayoutWithHierarchy {
  id: string;
  name: string;
  isDefault: boolean;
  isEmpty: boolean;
  /** Brak (stare dane) = aktywne. */
  isActive?: boolean;
  hierarchy: MenuItem[];
}

/**
 * Customowe menu tenanta ułożone w Kreatorze (GET /api/menu/layouts?withHierarchy=true) —
 * z zawartością (hierarchy) w JEDNYM fetchu. Kafelek „Customowe" paska obszarów (tryb drzewa,
 * `TenantMenuRail`) pokazuje każde niepuste, niedefaultowe, aktywne menu jako moduł z jego
 * kartotekami; Mapa dostępu tego zapytania nie używa (kolumna „Customowe" bierze kartoteki własne
 * z `useCustomModulesForMenu`). `staleTime` jak przy modułach — układ menu zmienia się rzadko.
 */
function useCustomMenuLayouts() {
  return useQuery({
    queryKey: ['tenant', 'menu', 'layouts', 'with-hierarchy'],
    queryFn: async () => {
      const res = await api.get<{ layouts: MenuLayoutWithHierarchy[] }>(
        '/menu/layouts?withHierarchy=true',
      );
      return res.data.layouts;
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Krok 3 kreatora modułów — rodzaje danych z zainstalowanych szablonów, pogrupowane
// po obszarze (HR, Finanse…). Pobiera GET /api/my/installed-modules. Każdy rodzaj
// danych = osobna pozycja menu linkująca do /modules/{slug}. Backend zwraca TYLKO
// rodzaje z modułów z aktywną/trialową subskrypcją (gating po wykupieniu modułu).
// Brak obszarów / błąd → silent (nie renderuj nic).
// ---------------------------------------------------------------------------

export interface InstalledMenuItem {
  /**
   * Dyskryminator. 'system' = moduł platformowy (slug = kod modułu), 'template' = rodzaj
   * danych z kreatora. 'section'/'link'/'separator' = węzły fragmentu domyślnego menu modułu
   * (kreator/hub) — sekcja z dziećmi, custom link, separator.
   */
  kind: 'system' | 'template' | 'section' | 'link' | 'separator';
  slug: string;
  name: string;
  icon: string | null;
  /** kind 'link' — adres docelowy custom linku. */
  url?: string;
  /** kind 'link' — otwórz w nowej karcie. */
  external?: boolean;
  /** kind 'section' — zagnieżdżone pozycje. */
  children?: InstalledMenuItem[];
  /**
   * Kartoteka na bazie klasy dokumentu (encja `document_class`, np. Teczka → dokumenty
   * pracownicze) — UUID systemowego typu dokumentu, do którego TA encja jest zawężona.
   * Obecne tylko dla `kind:'system'` rozwiązanego z encji `document_class`. Łączone z
   * `documentTypeIds` (poniżej) i dopisywane jako `?documentTypeIds=` do trasy kartoteki
   * przez `installedLeafPath` — jedyne miejsce liczące ścieżkę liścia, wołane przez
   * `InstalledMenuItemNode` (render domyślny obszaru + `installed_area` w Kreatorze) i
   * przez `RenderMenuItem` (`item.type === 'module'`, własny układ Kreatora — tam też
   * liczy `baseTarget` pod `?mi=`).
   */
  documentTypeId?: string;
  /**
   * Zbiór id typów dokumentów, do których TA encja jest zawężona filtrem stałym
   * pozycji menu fragmentu (`node.view.baseFilters` — filtr `document_type_id in […]`).
   * Obecne tylko gdy fragment niesie taki filtr; może współistnieć z legacy
   * `documentTypeId` (encja `document_class` ma zawsze jeden typ narzucony przez encję,
   * NIEZALEŻNIE od tego zbioru — oba się nie wykluczają). Dopisywane jako
   * `?documentTypeIds=` do trasy kartoteki przez `installedLeafPath`.
   */
  documentTypeIds?: string[];
  /**
   * Kod modułu-właściciela tej pozycji (stemplowany przez backend na pozycjach TOP-LEVEL
   * w `area.items[]`). Podstawa grupowania w przełączniku widoku menu — patrz
   * `groupInstalledItemsByModule` niżej.
   */
  moduleCode?: string;
  /** Nazwa modułu-właściciela — etykieta w przełączniku widoku menu. */
  moduleName?: string;
  /** Ikona modułu-właściciela (z katalogu) — preferowana w przełączniku widoku menu. */
  moduleIcon?: string | null;
  /**
   * Liść wskazuje kartotekę/ekran z modułu, którego klient nie ma (menu modułu z kreatora ITE
   * może wskazywać cele innych modułów). Render: wyszarzony, bez linku, z plakietką.
   */
  inactive?: true;
  /** kind 'link' — element systemowy „Ustawienia" wstawiony do menu modułu. */
  systemItem?: 'settings';
  /** Id pozycji w menu modułu — niesione tylko przy przypisanym widoku; trafia do `?mi=`. */
  menuItemId?: string;
  /** Przypięty formularz: `'<slug kartoteki>:<id układu>'` (format klienta). */
  viewRef?: string;
  /** Przypięta lista: `'<slug kartoteki>:<id układu>'` (format klienta). */
  listRef?: string;
  /** Stałe filtry / sortowanie pozycji. */
  view?: MenuItemView;
}

export interface InstalledMenuArea {
  code: string;
  name: string;
  icon: string | null;
  color: string | null;
  items: InstalledMenuItem[];
}

/**
 * Obszary zainstalowanych modułów (GET /api/my/installed-modules). Wspólne
 * zapytanie sekcji obszarów (fallback) i renderera własnego układu (hub Krok 4 —
 * element „obszar zainstalowany"); w trybie własnego układu renderer montuje
 * je samodzielnie, bo InstalledModulesMenu wtedy nie startuje. Hook (razem z zapytaniem o
 * obowiązujący typ wniosku o nieobecność — `useHrAbsenceDocumentTypeQuery`, skrót „Nieobecności"
 * w HrQuickNav) żyje w `hooks/useInstalledModulesMenuQuery`, żeby hooki stron mogły go czytać
 * bez cyklicznego importu paska bocznego.
 */
export { useInstalledModulesMenuQuery };

function pathnameOf(path: string): string {
  return path.split('?')[0] ?? path;
}

/**
 * Klucz tożsamości pozycji menu w mapach ikon/etykiet zakładek: ścieżka + parametry, które
 * odróżniają dwie pozycje wskazujące TĘ SAMĄ trasę — zawężenie do typów dokumentu
 * (`documentTypeIds`, razem z legacy `documentTypeId`, posortowane) oraz `mi` (pozycja
 * własnego układu z przypisanym widokiem). Dzięki temu „Wnioski urlopowe"
 * (`/special-docs?documentTypeIds=<WU>`) i „Dokumenty specjalne" (`/special-docs`) mają osobne
 * wpisy zamiast nadpisywać się nawzajem. Pozostałe parametry zapytania (paginacja, sortowanie
 * listy…) są pomijane, by zakładka z dopiskami stanu listy nadal trafiała w swój wpis.
 */
export function menuIdentityKey(path: string): string {
  const queryStart = path.indexOf('?');
  if (queryStart === -1) return path;
  const pathname = path.slice(0, queryStart);
  const params = new URLSearchParams(path.slice(queryStart + 1));
  const typeIds = [
    ...new Set(
      [...(params.get('documentTypeIds') ?? '').split(','), params.get('documentTypeId') ?? '']
        .map((id) => id.trim())
        .filter((id) => id.length > 0),
    ),
  ].sort();
  const menuItemId = params.get('mi');
  const identity: string[] = [];
  if (typeIds.length > 0) identity.push(`documentTypeIds=${typeIds.join(',')}`);
  if (menuItemId) identity.push(`mi=${menuItemId}`);
  return identity.length > 0 ? `${pathname}?${identity.join('&')}` : pathname;
}

/**
 * Odczyt z mapy ikon/etykiet zakładek dla PEŁNEJ ścieżki karty (z query): najpierw wpis
 * pozycji o tej samej tożsamości (ścieżka + zawężenie, `menuIdentityKey`), potem sama
 * ścieżka jako fallback. Pod samą ścieżką leży wyłącznie pozycja bez parametrów tożsamości
 * (`setTabMeta`) — karta z zawężeniem lub `?mi=`, której nie ma w menu, dostaje nazwę pozycji
 * bez parametrów albo (brak wpisu) nazwę domyślną karty. Konsument mapy (pasek kart) woła to
 * zamiast `map.get(pathname)`.
 */
export function lookupMenuMeta(
  map: ReadonlyMap<string, string>,
  tabPath: string,
): string | undefined {
  return map.get(menuIdentityKey(tabPath)) ?? map.get(pathnameOf(tabPath));
}

/**
 * Zapis pozycji menu do mapy zakładek — WYŁĄCZNIE pod kluczem tożsamości (`menuIdentityKey`).
 * Samą ścieżkę zajmuje tylko pozycja BEZ parametrów tożsamości; pozycja z zawężeniem do typów
 * dokumentu (np. „Wnioski urlopowe" → `/special-docs?documentTypeIds=…`) albo z przypisanym
 * widokiem własnego układu (`?mi=`, także gdy widok niczego nie zawęża) jej NIE zajmuje — goły
 * `/special-docs` pokazuje wtedy pozycję bez parametrów albo nazwę domyślną karty. Nazwa karty
 * ma odpowiadać temu, co pokazuje lista: adres bez parametrów to lista bez zawężenia i bez
 * przypisanego widoku, więc nie może nosić nazwy takiej pozycji.
 */
function setTabMeta(map: Map<string, string>, path: string, value: string): void {
  map.set(menuIdentityKey(path), value);
}

/**
 * Mapa `trasa → nazwa ikony` z menu tenanta — JEDNO źródło ikon dla sidebara i paska kart.
 * Zakładka bierze stąd ikonę (przez `resolveIcon`), więc taby zawsze zgadzają się z lewym
 * menu, a same ikony ustawiasz w konfiguracji menu (seed / Kreator menu), nie na sztywno.
 * Klucz = ścieżka + zawężenie do typów dokumentu / pozycja układu (`menuIdentityKey`),
 * odczyt przez `lookupMenuMeta`; pod samą ścieżką tylko pozycja bez zawężenia. Custom layout ma
 * priorytet nad menu modułowym.
 * Gate `enabled` — wołane też z paska kart wspólnego z adminem; poza tenantem = pusta mapa.
 *
 * TRADE-OFFY (świadome, z łagodnym fallbackiem na `tab.icon`):
 *  - Dwie pozycje na tę samą trasę różnią się ikoną tylko wtedy, gdy różni je zawężenie
 *    (`?documentTypeIds=`) lub `?mi=`; pozostałe parametry zapytania są ignorowane.
 *  - Warstwa 3 (custom layout) liczy trasę pozycji-modułu uproszczoną formułą
 *    (`MODULE_LANDING_OVERRIDES`); dla modułu o dedykowanej trasie z liścia zainstalowanego
 *    obszaru override ikony z custom layoutu może nie trafić — wtedy zostaje ikona z warstwy 2
 *    (modułowej) albo domyślna. Bezpieczne (żadnego crasha), tylko mniej „idealne".
 *  - Karta, której adres nie niesie parametrów tożsamości (np. powrót na listę z karty
 *    otwartej spoza listy — wyszukiwarka, link z innego rekordu; powrót z karty otwartej
 *    z listy niesie je przez `useWithMenuItem`), dostaje ikonę/nazwę pozycji bez parametrów,
 *    a gdy takiej w menu nie ma — domyślną ze słownika kart (świadomie: nazwa zgodna ze
 *    stanem listy).
 */
export function useTenantMenuIconMap(enabled: boolean): Map<string, string> {
  const layoutQuery = useActiveMenuLayout({ enabled });
  const installedQuery = useInstalledModulesMenuQuery({ enabled });
  return useMemo(() => {
    const map = new Map<string, string>();
    if (!enabled) return map;
    const sysItem = new Map(SYSTEM_ITEMS.map((s) => [s.itemKey, s]));

    // Warstwa 1 (baza) — domyślne ikony pozycji systemowych (pinned base: Użytkownicy/
    // Firmy/Ustawienia itd.), których nie ma w areach ani custom.
    for (const s of SYSTEM_ITEMS) if (s.iconName) map.set(pathnameOf(s.path), s.iconName);

    // Warstwa 2 — menu modułowe (zainstalowane obszary): nadpisuje bazę ikoną z definicji
    // modułu (np. Pracownicy = teczka zamiast domyślnego BadgeCheck).
    const walkInstalled = (items: InstalledMenuItem[]) => {
      for (const it of items) {
        const p = installedItemPath(it);
        if (p && it.icon) setTabMeta(map, p, it.icon);
        if (it.children?.length) walkInstalled(it.children);
      }
    };
    for (const area of installedQuery.data ?? []) walkInstalled(area.items);

    // Warstwa 3 (najwyższa) — własny layout menu: nadpisuje wszystko override'em `iconName`.
    const walkCustom = (items: MenuItem[]) => {
      for (const it of items) {
        let route: string | undefined;
        let icon: string | undefined = it.iconName ?? undefined;
        if (it.type === 'system_item' && it.itemKey) {
          const def = sysItem.get(it.itemKey);
          route = def?.path;
          icon = icon ?? def?.iconName;
        } else if (it.type === 'module' && it.moduleCode) {
          route = MODULE_LANDING_OVERRIDES[it.moduleCode] ?? `/modules/${it.moduleCode}`;
        } else if (it.type === 'custom_link' && it.url?.startsWith('/')) {
          route = it.url;
        }
        if (route && icon) setTabMeta(map, customItemTabPath(it, route), icon);
        if (it.children?.length) walkCustom(it.children);
      }
    };
    walkCustom((layoutQuery.data?.layout?.hierarchy as MenuItem[] | undefined) ?? []);
    return map;
  }, [enabled, layoutQuery.data, installedQuery.data]);
}

/**
 * Mapa `trasa → etykieta` z menu tenanta — bliźniacza do `useTenantMenuIconMap`. Pasek kart
 * bierze stąd NAZWĘ zakładki, więc karta modułu (`/modules/<slug>`) i każdej kartoteki nosi
 * etykietę klikniętej pozycji menu (spójność tab↔lewe menu; etykietę ustawiasz w konfiguracji
 * menu / Kreatorze, nie na sztywno), a nie generyczne „Moduły" ze słownika ścieżek. Klucz =
 * ścieżka + zawężenie do typów dokumentu / pozycja układu (`menuIdentityKey`), odczyt przez
 * `lookupMenuMeta` — dwie pozycje na `/special-docs` zawężone do innych typów mają osobne
 * etykiety; pod samą ścieżką tylko pozycja bez zawężenia (zawężona nie przejmuje gołego adresu).
 * Custom layout ma priorytet nad menu modułowym.
 *
 * Warstwa bazowa (Użytkownicy/Firmy/Ustawienia) NIE jest tu wpisywana — te trasy mają dobre
 * etykiety w słowniku kart (`resolveTenantTabMeta`); mapa dopina tylko liście modułów/kartotek.
 * Trasy szczegółów (`/contractors/<id>`) też nie trafiają do mapy (menu ma tylko listę), więc
 * podmiana nazwy rekordu przez `updateTabLabel` pozostaje nietknięta.
 */
export function useTenantMenuLabelMap(enabled: boolean): Map<string, string> {
  const layoutQuery = useActiveMenuLayout({ enabled });
  const installedQuery = useInstalledModulesMenuQuery({ enabled });
  return useMemo(() => {
    const map = new Map<string, string>();
    if (!enabled) return map;

    // Warstwa modułowa (zainstalowane obszary) — etykieta liścia = jego nazwa z menu.
    const walkInstalled = (items: InstalledMenuItem[]) => {
      for (const it of items) {
        const p = installedItemPath(it);
        if (p && it.name) setTabMeta(map, p, it.name);
        if (it.children?.length) walkInstalled(it.children);
      }
    };
    for (const area of installedQuery.data ?? []) walkInstalled(area.items);

    // Warstwa własnego layoutu (najwyższa) — etykieta z pozycji Kreatora nadpisuje modułową.
    const walkCustom = (items: MenuItem[]) => {
      for (const it of items) {
        let route: string | undefined;
        if (it.type === 'module' && it.moduleCode) {
          route = MODULE_LANDING_OVERRIDES[it.moduleCode] ?? `/modules/${it.moduleCode}`;
        } else if (it.type === 'custom_link' && it.url?.startsWith('/')) {
          route = it.url;
        }
        // system_item pomijamy — baza ma etykietę ze słownika kart.
        if (route && it.label) setTabMeta(map, customItemTabPath(it, route), it.label);
        if (it.children?.length) walkCustom(it.children);
      }
    };
    walkCustom((layoutQuery.data?.layout?.hierarchy as MenuItem[] | undefined) ?? []);
    return map;
  }, [enabled, layoutQuery.data, installedQuery.data]);
}

/** Jeden zakupiony moduł (menu view) — grupa pozycji top-level z tego samego `moduleCode`. */
export interface InstalledMenuModuleGroup {
  code: string;
  name: string;
  icon: string | null;
  items: InstalledMenuItem[];
}

/**
 * Grupuje płaskie pozycje top-level ze WSZYSTKICH obszarów po `moduleCode` — jeden
 * wpis na zakupiony moduł, w kolejności pierwszego wystąpienia (czyli kolejności
 * odpowiedzi backendu: obszary sortowane po sort_order, pozycje w obszarze też).
 * Pozycje bez `moduleCode` (nie powinno wystąpić — backend stempluje wszystkie
 * top-level pozycje, ale defensywnie) są pomijane — nie tworzą „modułu widmo".
 */
export function groupInstalledItemsByModule(
  areas: InstalledMenuArea[],
): InstalledMenuModuleGroup[] {
  const order: string[] = [];
  const byCode = new Map<string, InstalledMenuModuleGroup>();

  for (const area of areas) {
    for (const item of area.items) {
      if (!item.moduleCode) continue;
      let group = byCode.get(item.moduleCode);
      if (!group) {
        group = {
          code: item.moduleCode,
          name: item.moduleName ?? item.moduleCode,
          icon: item.moduleIcon ?? item.icon,
          items: [],
        };
        byCode.set(item.moduleCode, group);
        order.push(item.moduleCode);
      }
      group.items.push(item);
    }
  }

  return order.map((code) => byCode.get(code)!);
}

/**
 * Widoki menu tenanta (kolorowe ikony paska) — NATURALNA kolejność, po filtrze uprawnień
 * (menu per user/rola/zespół), BEZ osobistych ustawień paska. Wspólne źródło dla paska
 * (`TenantSidebar`) i ekranu Ustawienia → Pasek boczny, żeby oba widziały tę samą listę.
 */
export function useTenantMenuViews() {
  const { t } = useTranslation();
  const installedQuery = useInstalledModulesMenuQuery();
  // Referencja stabilna między renderami (dopóki `installedQuery.data` się nie zmieni) —
  // `?? []` bez memo tworzyłoby NOWĄ tablicę przy każdym renderze gdy `data` jest
  // undefined (loading/error), co rozjeżdżałoby deps efektu w `useTenantMenuViewSelection`
  // (efekt reaguje na referencję `modules`, nie tylko na treść — patrz jej komentarz).
  const areas = useMemo(() => installedQuery.data ?? [], [installedQuery.data]);

  // F24 menu per user — widoczność menu modułów zalogowanego usera. `allowed=null` = pełen
  // dostęp (super-user / brak nadpisania); inaczej pokazujemy tylko przypisane kody modułów.
  const { me } = useTenantAuth();
  const allowedMenu = me?.menuModules?.allowed ?? null;
  const defaultMenuCode = me?.menuModules?.default ?? null;
  // Widoki przełącznika = MODUŁY (decyzja Eugenii 2026-08-06, cofa „widok = obszar" z
  // 2026-07-31). Jeden wykupiony moduł = jeden widok w przełączniku, nazwany nazwą modułu
  // (crm_core → „Systemowe", processes → „Procesy", ksef → „Integracja KSeF", Magazyny →
  // „Magazyny", Delegacje → „Delegacje"…). Obszar żyje wyłącznie po stronie admina (porządek
  // katalogu), u tenanta NIE jest poziomem nawigacji.
  const moduleGroups = useMemo(() => {
    const groups = groupInstalledItemsByModule(areas);
    if (!allowedMenu) return groups; // pełen dostęp
    const isAllowed = menuModuleAllowedPredicate(allowedMenu);
    return groups.filter((g) => isAllowed(g.code));
  }, [areas, allowedMenu]);

  // Widok „Kadry" (skróty HR: pulpit/Nieobecności/Grafik/Teczka/Pracownicy/Struktura) NIE
  // jest stałym blokiem — pojawia się jako WIDOK w przełączniku tylko gdy firma ma co
  // najmniej jeden moduł z obszaru HR (obszar `hr` z pozycjami), a osoba ma choć jeden z nich
  // w menu. Bez modułu HR (np. firma bez Kadr) widoku nie ma. Wewnątrz widoku skróty oparte na
  // module (Grafik←`grafiki`, Teczka←`teczka`, Nieobecności←moduł-właściciel liścia o typie
  // z flagi katalogu) są dodatkowo przecinane z tą samą listą `allowedMenu` — patrz
  // `HrQuickNav`; pulpit/Pracownicy/Struktura nie mają bramki modułu.
  const hasHrArea = useMemo(() => {
    const hrArea = areas.find((a) => a.code === HR_AREA_CODE);
    if (!hrArea || hrArea.items.length === 0) return false;
    if (!allowedMenu) return true;
    // Widok „Kadry" tylko gdy user ma przypisany co najmniej jeden moduł z obszaru HR.
    const isAllowed = menuModuleAllowedPredicate(allowedMenu);
    return hrArea.items.some((it) => isAllowed(it.moduleCode));
  }, [areas, allowedMenu]);
  const views = useMemo<InstalledMenuModuleGroup[]>(
    () =>
      hasHrArea
        ? [
            { code: HR_VIEW_CODE, name: t('hr.nav.section'), icon: 'Users', items: [] },
            ...moduleGroups,
          ]
        : moduleGroups,
    [hasHrArea, moduleGroups, t],
  );

  return { areas, views, allowedMenu, defaultMenuCode, isLoading: installedQuery.isLoading };
}

/**
 * Grupuje pozycje po OBSZARZE — jeden wpis na obszar (Systemowe / Dokumenty / Kadry…),
 * zamiast jednego na moduł. Decyzja produktowa 2026-07-31: u tenanta poziomem nawigacji
 * jest OBSZAR, nie pojedynczy moduł. Wcześniej przełącznik pokazywał każdy moduł osobno
 * („Procesy", „Integracja KSeF", „Faktury", „Pisma"…), co dublowało pozycje i rozbijało
 * dokumenty na 5 widoków. Teraz: widok „Systemowe" = kartoteki crm_core + Decyzje + KSeF,
 * widok „Dokumenty" = wszystkie klasy dokumentów.
 *
 * Kolejność obszarów = kolejność z backendu (sort_order), kolejność pozycji w obszarze
 * zachowana. Obszary bez pozycji pomijane (nie tworzą pustego widoku).
 *
 * Nazwa widoku (2026-08-03): gdy obszar zawiera dokładnie JEDEN moduł, widok bierze nazwę
 * MODUŁU zamiast obszaru — np. moduł „Serwis" zainstalowany w obszarze „Finanse" pokazuje
 * się u tenanta jako widok „Serwis", nie „Finanse". Obszary z wieloma modułami (Systemowe =
 * crm_core+Decyzje+KSeF, Dokumenty = wiele klas) zostają przy nazwie obszaru, żeby nie
 * dublować/rozbijać widoków. Kod widoku (selekcja) niezmiennie = kod obszaru.
 */
export function groupInstalledItemsByArea(areas: InstalledMenuArea[]): InstalledMenuModuleGroup[] {
  return areas
    .filter((area) => area.items.length > 0)
    .map((area) => {
      const moduleCodes = new Set(
        area.items.map((it) => it.moduleCode).filter((c): c is string => Boolean(c)),
      );
      let name = area.name ?? area.code;
      if (moduleCodes.size === 1) {
        const moduleName = area.items.find((it) => it.moduleCode)?.moduleName;
        if (moduleName) name = moduleName;
      }
      return { code: area.code, name, icon: area.icon, items: area.items };
    });
}

/**
 * Grupuje pozycje JEDNEGO obszaru po `moduleCode` — poziom „moduł" w drzewku menu
 * (obszar → moduł → kartoteka). W kolejności pierwszego wystąpienia (backend sortuje
 * pozycje w obszarze). Pozycje bez `moduleCode` (defensywnie) lądują we wspólnej grupie
 * o kodzie `__nomodule__`, nazwanej jak obszar — żeby nic nie zginęło z drzewka.
 */
function groupAreaItemsByModule(area: InstalledMenuArea): InstalledMenuModuleGroup[] {
  const order: string[] = [];
  const byCode = new Map<string, InstalledMenuModuleGroup>();
  for (const item of area.items) {
    const code = item.moduleCode ?? '__nomodule__';
    let group = byCode.get(code);
    if (!group) {
      group = {
        code,
        name: item.moduleName ?? area.name,
        icon: item.moduleIcon ?? item.icon,
        items: [],
      };
      byCode.set(code, group);
      order.push(code);
    }
    group.items.push(item);
  }
  return order.map((code) => byCode.get(code)!);
}

/**
 * Pojedynczy NAWIGOWALNY liść (nie nagłówek sekcji / separator). Gdy moduł albo obszar
 * jednomodułowy sprowadza się do JEDNEJ kartoteki, flyout jest zbędny (panel z jedną pozycją
 * dublującą nazwę) — wiążemy wprost linkiem. Zwraca ten liść albo null (0, >1, albo sekcja).
 */
function singleNavLeaf(items: InstalledMenuItem[]): InstalledMenuItem | null {
  if (items.length !== 1) return null;
  const it = items[0]!;
  // Liść nieaktywny nie jest celem nawigacji — zostaje flyout z wyszarzoną pozycją.
  return it.kind === 'section' || it.kind === 'separator' || it.inactive ? null : it;
}

/**
 * Spłaszcza pozycje obszaru do listy NAWIGOWALNYCH liści (kartotek) — schodzi przez sekcje,
 * pomija separatory i puste węzły. Używane przez „Mapę dostępu" (widok nr 3), żeby każdy obszar
 * był wyliczony do końca (do kartotek), niezależnie od tego, czy backend zwrócił je płasko czy
 * w sekcjach modułów. Deduplikacja po ścieżce liścia (ta sama kartoteka nie dubluje się).
 */
function flattenAreaLeaves(
  items: InstalledMenuItem[],
  /** Mapa obszarów pokazuje także pozycje niewykupione (szare); pasek je pomija. */
  includeInactive = false,
): InstalledMenuItem[] {
  const out: InstalledMenuItem[] = [];
  const seen = new Set<string>();
  const walk = (list: InstalledMenuItem[]) => {
    for (const it of list) {
      if (it.kind === 'section') {
        walk(it.children ?? []);
      } else if (it.kind !== 'separator' && (includeInactive || !it.inactive)) {
        const key = installedLeafPath(it) ?? it.slug;
        if (key && !seen.has(key)) {
          seen.add(key);
          out.push(it);
        }
      }
    }
  };
  walk(items);
  return out;
}

/**
 * Paleta kolorów ikon obszarów w szklanych kafelkach paska — BARWY Z LOGO Workflow365
 * (niebieski / zieleń / pomarańcz) + akcenty. Format „R G B" (spacja) pod zmienną CSS `--c`
 * w `.rail-glass`. Cykl po kolejnych obszarach. */
/** Widok paska z menu customowego: moduł-podobna grupa + kolor wybrany w Kreatorze (null = wg kolejności). */
export type CustomRailView = InstalledMenuModuleGroup & { colorVar: string | null };

/** Prefiks kodu widoku odróżniający menu customowe (Kreator) od modułu w pasku widoków. */
const CUSTOM_VIEW_PREFIX = 'custom:';
/** Domyślna ikona menu customowego w pasku (własna ikona z Kreatora — osobny krok). */
const CUSTOM_VIEW_ICON = 'MenuSquare';

const RAIL_ICON_COLORS = [
  '31 111 214', // niebieski (logo)
  '63 185 80', // zielony (logo)
  '242 106 27', // pomarańczowy (logo)
  '124 92 255', // fiolet (akcent)
  '14 165 183', // turkus (akcent)
  '236 72 153', // róż (akcent)
  '234 179 8', // bursztyn (akcent)
];

/**
 * Menu customowe z Kreatora dostępne zalogowanemu userowi (kaskada uprawnień) — jako widoki paska
 * (moduł-podobne grupy z własnym kolorem/ikoną). Puste i nieaktywne menu odsiane (nie ma czego
 * pokazać / wyłączone w Kreatorze). Wspólne źródło dla paska (`TenantSidebar`) i konfiguratora
 * (Ustawienia → Pasek boczny), żeby oba widziały tę samą listę menu customowych.
 */
export function useCustomRailViews(): {
  customViews: CustomRailView[];
  isLoading: boolean;
  /** Nieudany odczyt bez danych w ręku — lista menu customowych jest wtedy NIEZNANA (nie pusta). */
  isError: boolean;
  retry: () => void;
} {
  const availableLayoutsQuery = useAvailableMenuLayouts();
  const customViews = useMemo<CustomRailView[]>(
    () =>
      (availableLayoutsQuery.data?.layouts ?? [])
        .filter((l) => !l.isEmpty && l.isActive !== false)
        .map((l) => {
          const color = parseMenuColor(l.color);
          return {
            code: `${CUSTOM_VIEW_PREFIX}${l.id}`,
            name: l.name,
            icon: l.icon ?? CUSTOM_VIEW_ICON,
            items: [],
            colorVar: color ? MENU_COLOR_RGB[color] : null,
          };
        }),
    [availableLayoutsQuery.data],
  );
  return {
    customViews,
    isLoading: availableLayoutsQuery.isLoading,
    isError: availableLayoutsQuery.isError && availableLayoutsQuery.data === undefined,
    retry: () => void availableLayoutsQuery.refetch(),
  };
}

/**
 * Kolor kolorowej ikony paska dla DOWOLNEGO widoku (moduł albo menu customowe) — po NATURALNEJ
 * pozycji w scalonej liście (moduły, potem menu customowe), więc osobiste przestawienie barw nie
 * zmienia. Menu customowe z własnym kolorem z Kreatora (`colorVar`) biorą go; bez koloru — fallback
 * wg pozycji. Moduły są pierwsze, więc ich barwy zależą tylko od kolejności modułów (dodanie menu
 * customowego ich nie przesuwa).
 */
export function combinedRailColorLookup(
  combinedNatural: { code: string; colorVar?: string | null }[],
): (code: string) => string {
  const byCode = new Map<string, string>();
  combinedNatural.forEach((v, i) => {
    byCode.set(v.code, v.colorVar ?? RAIL_ICON_COLORS[i % RAIL_ICON_COLORS.length]!);
  });
  return (code) => byCode.get(code) ?? RAIL_ICON_COLORS[0]!;
}

/**
 * Ikona widoku paska (moduł albo menu customowe) — menu customowe (prefiks `custom:`) może mieć
 * dowolną ikonę z pełnej biblioteki lucide; moduł korzysta z mapy pickera/sidebara. Wspólne dla
 * paska (`RailModuleButton`) i konfiguratora (Ustawienia → Pasek boczny), żeby ikony się zgadzały.
 */
export function resolveRailViewIcon(code: string, icon: string | null): LucideIcon {
  const fullIcon = code.startsWith(CUSTOM_VIEW_PREFIX) ? resolveLucideIcon(icon) : undefined;
  return fullIcon ?? resolvePickerIcon(icon) ?? resolveIcon(icon, Package);
}

/**
 * Ikonowy przycisk w wąskim, floating pasku obszarów (widok nr 2) — SZKLANY KAFELEK
 * (glassmorphism, decyzja Eugenii 2026-09-16). Kolor kafelka podaje `colorVar` („R G B"),
 * brak = neutralny szary. Aktywny (panel otwarty) → mocniejszy blask (`rail-glass-on`).
 * `<div role="button">` (bramka UI blokuje surowe `<button>`), klawiatura + hover/focus otwiera kaskadę.
 */
function RailIconButton({
  active,
  title,
  colorVar,
  onActivate,
  onHover,
  onHoverEnd,
  children,
  testId,
}: {
  active?: boolean;
  title: string;
  colorVar?: string;
  onActivate?: (e: ReactMouseEvent<HTMLElement> | ReactKeyboardEvent<HTMLElement>) => void;
  onHover?: (e: ReactMouseEvent<HTMLElement> | ReactFocusEvent<HTMLElement>) => void;
  onHoverEnd?: () => void;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={title}
      aria-haspopup="menu"
      onClick={onActivate}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      onFocus={onHover}
      onBlur={onHoverEnd}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onActivate) {
          e.preventDefault();
          onActivate(e);
        }
      }}
      className={cn('rail-glass cursor-pointer', active && 'rail-glass-on')}
      style={colorVar ? ({ '--c': colorVar } as CSSProperties) : undefined}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/**
 * Wspólna „pigułka" tooltipa paska LISTY (wg Figmy, prawy kadr „hover"): PEŁNY kolor modułu +
 * biały tekst dla kafelków modułów, jasna z ciemnym tekstem dla kafelków neutralnych; lewy
 * dzióbek-strzałka kotwiczy pigułkę do ikony. Renderowana portalem (fixed) — overflow paska jej
 * nie przycina. `colorVar` („R G B") = pigułka modułu; brak = neutralna.
 */
function RailHoverPill<T extends HTMLElement>({
  anchorRef,
  railRef,
  show,
  label,
  colorVar,
}: {
  anchorRef: RefObject<T | null>;
  /** Ref paska — pigułka kotwiczy się do jego PRAWEJ krawędzi (nie nachodzi na pasek, kolumna
   *  pigułek wyrównana). Pion bierze z ikony (`anchorRef`). Brak → fallback do prawej ikony. */
  railRef?: RefObject<HTMLElement | null>;
  show: boolean;
  label: string;
  colorVar?: string;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!show) {
      setPos(null);
      return;
    }
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const railRight = railRef?.current?.getBoundingClientRect().right;
    setPos({ top: r.top + r.height / 2, left: (railRight ?? r.right) + 10 });
  }, [show, anchorRef, railRef]);
  if (!pos) return null;
  // Ikony modułów = tło w kolorze modułu. Ikony neutralne (Dashboard/Kalendarz/Mapa u góry,
  // Użytkownicy/Firmy/Ustawienia na dole) mają szary kafelek `.rail-tile` (#8f9bae) — dymek
  // dostaje ten sam szary, biały tekst i bez obwódki, żeby pasował do kafelka (decyzja Eugenii
  // 2026-09-21). Wcześniej neutralne dymki były białe z ciemnym tekstem.
  const solid = colorVar ? `rgb(${colorVar})` : '#8f9bae';
  return createPortal(
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translateY(-50%)',
        zIndex: 60,
        background: solid,
        color: '#ffffff',
      }}
      className="rail-fade-in pointer-events-none relative flex h-10 items-center whitespace-nowrap rounded-lg px-3.5 text-sm font-semibold shadow-md"
    >
      {/* Dzióbek-strzałka po lewej (kotwica do paska) — obrócony kwadrat w kolorze pigułki. */}
      <span
        aria-hidden="true"
        className="absolute -left-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rotate-45 rounded-[2px]"
        style={{ background: solid }}
      />
      {label}
    </div>,
    document.body,
  );
}

/** Ile kolorowych ikon obszarów widać naraz w pasku (decyzja właścicielki 2026-09-23). */
const RAIL_MAX_VISIBLE_ICONS = 8;
/** Ikona 40px + odstęp `gap-2` 8px — krok przewijania strzałką = jedna ikona. */
const RAIL_ICON_STEP_PX = 48;
/** Pionowy padding obszaru (`py-4`) — miejsce na cień ikon i powiększenie hover, żeby strzałki
 *  nie nachodziły na cień skrajnych ikon. */
const RAIL_SCROLL_PADDING_PX = 16;
/** Maks. wysokość obszaru: 8 ikon + 7 odstępów + padding góra/dół. */
const RAIL_SCROLL_MAX_HEIGHT_PX =
  RAIL_MAX_VISIBLE_ICONS * 40 + (RAIL_MAX_VISIBLE_ICONS - 1) * 8 + 2 * RAIL_SCROLL_PADDING_PX;

/**
 * Przewijana część paska z kolorowymi ikonami obszarów: pokazuje maks. 8 ikon; przy większej
 * liczbie nad i pod listą pojawiają się strzałki przewijające o jedną ikonę (kółko/dotyk działa
 * dalej, systemowy suwak ukryty — `.rail-scroll`). Strzałka jest aktywna tylko, gdy w danym
 * kierunku jest co przewijać; przy braku nadmiaru strzałek nie ma wcale.
 */
function RailScrollArea({ itemCount, children }: { itemCount: number; children: ReactNode }) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);
  const overflows = itemCount > RAIL_MAX_VISIBLE_ICONS || canUp || canDown;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanUp(el.scrollTop > 1);
      setCanDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [itemCount]);

  // Cel liczony od POPRZEDNIEGO celu (nie od bieżącej pozycji) — szybkie kolejne kliknięcia
  // w trakcie płynnej animacji sumują się, zamiast przerywać poprzedni krok.
  const targetRef = useRef<number | null>(null);
  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const from = targetRef.current ?? el.scrollTop;
    const snapped = Math.round(from / RAIL_ICON_STEP_PX) * RAIL_ICON_STEP_PX;
    const target = Math.max(0, Math.min(max, snapped + dir * RAIL_ICON_STEP_PX));
    targetRef.current = target;
    el.scrollTo({ top: target, behavior: 'smooth' });
    window.setTimeout(() => {
      if (targetRef.current === target) targetRef.current = null;
    }, 400);
  };

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
      {overflows && (
        <IconButton
          size="sm"
          aria-label={t('tenant.sidebar.railScrollUp')}
          onClick={() => scrollBy(-1)}
          disabled={!canUp}
          className="mb-3 shrink-0 cursor-pointer disabled:opacity-30"
          data-testid="tenant-list-rail-scroll-up"
        >
          <ChevronUp />
        </IconButton>
      )}
      <div
        ref={scrollRef}
        className="rail-scroll flex min-h-0 w-full flex-col items-center gap-2 overflow-y-auto py-4 [justify-content:safe_center]"
        style={{ maxHeight: RAIL_SCROLL_MAX_HEIGHT_PX }}
        data-testid="tenant-list-rail-scroll"
        data-overflow={overflows ? 'true' : undefined}
      >
        {children}
      </div>
      {overflows && (
        <IconButton
          size="sm"
          aria-label={t('tenant.sidebar.railScrollDown')}
          onClick={() => scrollBy(1)}
          disabled={!canDown}
          className="mt-3 shrink-0 cursor-pointer disabled:opacity-30"
          data-testid="tenant-list-rail-scroll-down"
        >
          <ChevronDown />
        </IconButton>
      )}
    </div>
  );
}

/**
 * Neutralny kafelek paska LISTY (Dashboard/Kalendarz/Mapa) = NavLink + neutralna pigułka na
 * najechanie ORAZ przy najechaniu na CAŁY pasek (`railHovered` — wszystkie tooltipy naraz).
 */
function RailPillTile({
  to,
  label,
  icon: Icon,
  railRef,
  wrapperClass = 'flex h-12 w-full flex-none items-center justify-center',
  testId,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  railRef: RefObject<HTMLElement | null>;
  wrapperClass?: string;
  testId?: string;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [self, setSelf] = useState(false);
  return (
    <div className={wrapperClass}>
      <NavLink
        ref={ref}
        to={to}
        aria-label={label}
        className="rail-tile"
        data-testid={testId}
        onMouseEnter={() => setSelf(true)}
        onMouseLeave={() => setSelf(false)}
        onFocus={() => setSelf(true)}
        onBlur={() => setSelf(false)}
      >
        <Icon />
      </NavLink>
      {/* Pigułka-tooltip pojawia się pod kursorem tylko przy najechaniu na tę ikonę (wariant 2). */}
      <RailHoverPill anchorRef={ref} railRef={railRef} show={self} label={label} />
    </div>
  );
}

/**
 * Kolorowa ikona modułu na pasku LISTY + jej pigułka (pełny kolor + biały tekst). Pigułka
 * pokazuje się pod kursorem tylko na najechanie/focus tej ikony (wariant 2 hover).
 */
function RailModuleButton({
  view,
  colorVar,
  active,
  railRef,
  onSelect,
}: {
  view: InstalledMenuModuleGroup;
  colorVar: string;
  active: boolean;
  railRef: RefObject<HTMLElement | null>;
  onSelect: (code: string) => void;
}) {
  // Ikony wybrane w pickerze (ITE dla modułu, kreator dla menu customowego) — wspólny resolver
  // z konfiguratorem (menu customowe może mieć dowolną ikonę z pełnej biblioteki lucide).
  const Icon = resolveRailViewIcon(view.code, view.icon);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [self, setSelf] = useState(false);
  return (
    <div
      className="flex w-full items-center justify-center"
      onMouseEnter={() => setSelf(true)}
      onMouseLeave={() => setSelf(false)}
      onFocus={() => setSelf(true)}
      onBlur={() => setSelf(false)}
    >
      <div
        ref={anchorRef}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(view.code)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(view.code);
          }
        }}
        aria-label={view.name}
        aria-pressed={active}
        data-testid={`list-rail-view-${view.code}`}
        className={cn('rail-glass cursor-pointer', active && 'rail-glass-on')}
        style={{ '--c': colorVar } as CSSProperties}
      >
        <Icon />
      </div>
      <RailHoverPill
        anchorRef={anchorRef}
        railRef={railRef}
        show={self}
        label={view.name}
        colorVar={colorVar}
      />
    </div>
  );
}

/**
 * Pasek boczny „dostępne menu" (tryb LISTA) — WĄSKI, FLOATING, ZAWSZE otwarty. Kolorowe ikony =
 * dostępne widoki (moduły/obszary tenanta); klik ikony wybiera widok, a panel listy obok pokazuje
 * jego kartoteki (domyślnie zaznaczony crm_core → „Systemowe"). U góry znak marki, pod nim ikona
 * ZWIŃ/ROZWIŃ panel listy, potem 3 neutralne kafelki (Dashboard/Kalendarz/Mapa obszarów), kolorowe
 * kafelki widoków i baza (Użytkownicy/Firmy/Ustawienia) na dole. Najechanie na cały pasek wyciemnia
 * resztę (scrim) i pokazuje wszystkie pigułki-tooltipy naraz. Panel listy sam jest zwijalny (ten
 * pasek zostaje) — decyzja Eugenii 2026-09-17.
 */
function TenantListRail({
  views,
  colorFor,
  railPrefs,
  activeCode,
  onSelect,
  collapsed,
  onToggleCollapse,
}: {
  /**
   * Scalona lista widoków paska (moduły + menu customowe) — JUŻ ułożona osobistymi ustawieniami
   * (kolejność + ukryte). Menu customowe rozpoznawane po prefiksie kodu (`custom:`), reszta to
   * moduły; oba renderują się tym samym kafelkiem.
   */
  views: CustomRailView[];
  /** Kod aktywnego widoku (moduł albo `custom:<id>`); null = brak zaznaczenia. */
  activeCode: string | null;
  /** Kolor ikony po kodzie widoku — z naturalnej pozycji (osobista kolejność go nie zmienia). */
  colorFor: (code: string) => string;
  /**
   * Osobiste ustawienia paska — tu tylko ukryte szare skróty (widoki przychodzą już ułożone).
   * `null` = układ jeszcze nieznany: rysujemy wyłącznie skróty niepodlegające ukryciu, żeby
   * ukryte nie mignęły przy starcie.
   */
  railPrefs: RailPreferences | null;
  onSelect: (code: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { t } = useTranslation();
  // Pigułka-tooltip pojawia się pod kursorem tylko na najechanie na KONKRETNĄ ikonę (decyzja
  // Eugenii 2026-09-21). `railRef` kotwiczy pigułki do prawej krawędzi paska (nie nachodzą na pasek).
  const railRef = useRef<HTMLElement>(null);
  const showTile = (key: RailTileKey) =>
    railPrefs ? isRailTileVisible(railPrefs, key) : RAIL_LOCKED_TILE_KEYS.includes(key);
  return (
    <aside
      ref={railRef}
      data-testid="tenant-list-rail"
      className={cn(
        'relative z-[45] my-3 ml-3 flex w-[72px] shrink-0 flex-col items-center border border-border-secondary bg-[#f1f1f4] pb-3 shadow-lg dark:bg-bg-secondary',
        // Panel otwarty → pasek sklejony z panelem: lewe rogi zaokrąglone, prawa krawędź płaska
        // (styk z panelem, jego lewa krawędź = szew). Zwinięty → samodzielny, w pełni zaokrąglony.
        collapsed ? 'rounded-2xl' : 'rounded-l-2xl border-r-0',
      )}
      style={{ height: 'calc(100vh - 1.5rem)' }}
    >
      {/* Marka — LOGO na tej samej wysokości co tekst „Workflow365" w panelu (blok 44px, tak jak
          marka w panelu). Pasek nie ma górnego paddingu, więc start treści = start panelu. */}
      {/* Logo — KWADRATOWY znak 40×40 z Figmy (nie ucięty wide-SVG). @ y≈20 (pasek py-20). */}
      <div
        className="flex h-[60px] w-full flex-none items-start justify-center pt-5"
        data-testid="tenant-list-rail-logo"
      >
        <img
          src={workflow365Mark}
          alt="Workflow365"
          className="h-10 w-10 rounded-[14px] object-cover"
        />
      </div>

      {/* Strzałka zwijania — w luce między logo a kafelkami (kafelki startują ~y116 wg Figmy).
          Bez tooltipa (decyzja właścicielki) — `aria-label` zostaje dla dostępności. */}
      <div className="flex h-[56px] w-full flex-none items-center justify-center">
        <div
          role="button"
          tabIndex={0}
          onClick={onToggleCollapse}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleCollapse();
            }
          }}
          aria-label={
            collapsed ? t('tenant.sidebar.expandPanel') : t('tenant.sidebar.collapsePanel')
          }
          aria-expanded={!collapsed}
          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          data-testid="tenant-list-rail-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </div>
      </div>

      {/* 3 kafelki: Dashboard / Kalendarz / Mapa obszarów (40×40, skok 48px = h-12; wg Figmy).
          Pigułka-tooltip pokazuje się na najechanie ORAZ przy najechaniu na cały pasek. */}
      {showTile('dashboard') && (
        <RailPillTile
          to="/dashboard"
          label={t('tenant.sidebar.dashboard')}
          icon={LayoutDashboard}
          railRef={railRef}
        />
      )}
      {showTile('calendar') && (
        <RailPillTile
          to="/calendar"
          label={t('tenant.calendar.title')}
          icon={Calendar}
          railRef={railRef}
        />
      )}
      {showTile('area-map') && (
        <RailPillTile
          to="/area-map"
          label={t('tenant.sidebar.accessMap.title')}
          icon={MapIcon}
          railRef={railRef}
          testId="tenant-list-rail-map"
        />
      )}

      {/* Kolorowe ikony DOSTĘPNYCH widoków (moduły + menu customowe w JEDNEJ liście, w kolejności
          z osobistych ustawień). Pełny kolor; tooltip z nazwą na najechanie (ikony nie mają
          podpisu). Aktywny = mocniejsza poświata (rail-glass-on). `w-full` — pełna szerokość paska,
          żeby overflow NIE obcinał cieni po bokach (parytet z górnymi/dolnymi ikonami). */}
      <RailScrollArea itemCount={views.length}>
        {views.map((view) => (
          <RailModuleButton
            key={view.code}
            view={view}
            colorVar={colorFor(view.code)}
            active={activeCode === view.code}
            railRef={railRef}
            onSelect={onSelect}
          />
        ))}
      </RailScrollArea>

      {/* Baza stała na dole paska — Użytkownicy / Firmy / Ustawienia. BEZ tooltipów (etykiety
          widoczne w panelu obok). */}
      <div className="flex w-full flex-col items-center gap-2">
        {SYSTEM_ITEMS.filter(
          (s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey) && showTile(s.itemKey as RailTileKey),
        ).map((def) => {
          const BaseIcon = resolveIcon(def.iconName, Package);
          const label = t(def.labelI18nKey, def.defaultLabel);
          return (
            <RailPillTile
              key={def.itemKey}
              to={def.path}
              label={label}
              icon={BaseIcon}
              railRef={railRef}
              wrapperClass="flex w-full items-center justify-center"
            />
          );
        })}
      </div>
    </aside>
  );
}

/**
 * Tryb menu nr 2 — wąski, FLOATING, zaokrąglony pasek IKON OBSZARÓW (zastąpił drzewko, decyzja
 * Eugenii 2026-09-16). Zastępuje całą powłokę sidebara. Ikony obszarów wyśrodkowane pionowo;
 * najechanie na obszar wysuwa panel (moduły albo kartoteki gdy obszar jednomodułowy), najechanie
 * na moduł — panel kartotek (dwupoziomowa kaskada na hover, opóźnione zamknięcie). Przy głębokich
 * obszarach (HR = 11 modułów) nie ma ściany pozycji. Data-driven z GET /api/my/installed-modules;
 * jednolite kolory (bez per-obszar). Custom layout i tryb zwinięty NIE używają paska (patrz caller).
 */
function TenantMenuRail({ areas }: { areas: InstalledMenuArea[] }) {
  const { t } = useTranslation();
  const customModulesQuery = useCustomModulesForMenu();
  const customMenusQuery = useCustomMenuLayouts();
  const [mapOpen, setMapOpen] = useState(false);
  // Kaskada: p1 = panel obszaru (moduły albo kartoteki gdy obszar jednomodułowy),
  // p2 = panel modułu (kartoteki). Oba na hover, z opóźnionym zamknięciem.
  const [p1, setP1] = useState<{
    key: string;
    label: string;
    area: InstalledMenuArea;
    modules: { mod: InstalledMenuModuleGroup; renderItems: InstalledMenuItem[] }[];
    top: number;
    left: number;
  } | null>(null);
  const [p2, setP2] = useState<{
    key: string;
    label: string;
    items: InstalledMenuItem[];
    top: number;
    left: number;
  } | null>(null);
  const p1Ref = useRef<HTMLDivElement>(null);
  const p2Ref = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };
  const closeAll = () => {
    setP1(null);
    setP2(null);
  };
  // Opóźnione zamknięcie (~160 ms) — czas przejechać myszą z ikony na panel / z p1 na p2.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(closeAll, 160);
  };

  const treeAreas = areas
    .map((area) => ({
      area,
      modules: groupAreaItemsByModule(area)
        .map((mod) => ({
          mod,
          renderItems: pruneEmptySections(stripPinnedBaseDuplicates(mod.items)),
        }))
        .filter((m) => m.renderItems.length > 0),
    }))
    .filter((a) => a.modules.length > 0);

  // Kafelek „Customowe" (spójnie z Mapą dostępu, ale pasek NIE jest kopią mapy): JEDEN kafelek,
  // którego flyout pokazuje customowe menu tenanta z Kreatora jako MODUŁY (Dokumenty, Serwis…) —
  // kaskada obszar→moduł(menu)→kartoteki. Dokumenty NIE są osobnym kafelkiem, tylko modułem w
  // „Customowe" (decyzja Eugenii). Pozycje menu rozwiązywane na liście przez `resolveMenuToLeaves`.
  const mkLeaf = (
    slug: string,
    name: string,
    icon: string | null,
    url: string,
  ): InstalledMenuItem => ({
    kind: 'link',
    slug,
    name,
    icon,
    url,
  });
  const baseBySlugTree = new Map(BASE_PACKAGE_KARTOTEKI.map((b) => [b.slug, b]));
  const customBySlugTree = new Map((customModulesQuery.data ?? []).map((m) => [m.slug, m]));
  const resolveMenuToLeaves = (items: MenuItem[]): InstalledMenuItem[] => {
    const out: InstalledMenuItem[] = [];
    for (const it of items) {
      if (it.type === 'separator') continue;
      if (it.type === 'section') {
        out.push(...resolveMenuToLeaves(it.children ?? []));
      } else if (it.type === 'module' && it.moduleCode) {
        const base = baseBySlugTree.get(it.moduleCode);
        const custom = customBySlugTree.get(it.moduleCode);
        const to = withMenuItemDocumentTypes(
          base?.path ?? MODULE_LANDING_OVERRIDES[it.moduleCode] ?? `/modules/${it.moduleCode}`,
          it,
        );
        out.push(
          mkLeaf(
            it.moduleCode,
            it.label ?? base?.label ?? custom?.name ?? it.moduleCode,
            base?.iconName ?? custom?.icon ?? it.iconName ?? null,
            to,
          ),
        );
      } else if (it.type === 'system_item' && it.itemKey) {
        const sys = SYSTEM_ITEMS.find((s) => s.itemKey === it.itemKey);
        out.push(
          mkLeaf(
            it.itemKey,
            it.label ?? (sys ? t(sys.labelI18nKey, sys.defaultLabel) : it.itemKey),
            sys?.iconName ?? null,
            sys?.path ?? '#',
          ),
        );
      } else if (it.type === 'custom_link' && it.url && classifyMenuLinkUrl(it.url) !== null) {
        out.push(mkLeaf(it.url, it.label ?? it.url, it.iconName ?? null, it.url));
      }
    }
    return out;
  };
  const customMenus = (customMenusQuery.data ?? []).filter(
    (l) => !l.isDefault && !l.isEmpty && l.isActive !== false,
  );
  const customEntry = {
    area: {
      code: '__custom__',
      name: t('tenant.sidebar.accessMap.customModules'),
      icon: 'Boxes',
      color: null,
      items: [],
    } as InstalledMenuArea,
    modules: customMenus
      .map((menu) => ({
        mod: { code: menu.id, name: menu.name, icon: 'Boxes', items: [] },
        renderItems: resolveMenuToLeaves(menu.hierarchy),
      }))
      .filter((m) => m.renderItems.length > 0),
  };
  // Kolejność kafelków: obszary → (Customowe, gdy są customowe menu).
  const railEntries = [...treeAreas, ...(customEntry.modules.length > 0 ? [customEntry] : [])];

  const openArea = (
    e:
      | ReactMouseEvent<HTMLElement>
      | ReactKeyboardEvent<HTMLElement>
      | ReactFocusEvent<HTMLElement>,
    entry: (typeof treeAreas)[number],
  ) => {
    cancelClose();
    setP2(null);
    const r = e.currentTarget.getBoundingClientRect();
    setP1({
      key: entry.area.code,
      label: entry.area.name,
      area: entry.area,
      modules: entry.modules,
      top: r.top,
      left: r.right + 10,
    });
  };

  const openModule = (
    e:
      | ReactMouseEvent<HTMLElement>
      | ReactKeyboardEvent<HTMLElement>
      | ReactFocusEvent<HTMLElement>,
    key: string,
    label: string,
    items: InstalledMenuItem[],
  ) => {
    cancelClose();
    const r = e.currentTarget.getBoundingClientRect();
    setP2({ key, label, items, top: r.top, left: r.right + 6 });
  };

  useEffect(() => () => cancelClose(), []);
  // Zamknięcie kaskady: klik poza paskiem/panelami, Escape.
  useEffect(() => {
    if (!p1) return;
    const onDown = (ev: MouseEvent) => {
      const el = ev.target as HTMLElement;
      if (
        !p1Ref.current?.contains(el) &&
        !p2Ref.current?.contains(el) &&
        !el.closest?.('[data-testid="tenant-sidebar-rail"]')
      ) {
        closeAll();
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeAll();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [p1]);

  const pinned = SYSTEM_ITEMS.filter((s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey));
  // Stałe ikony (Dashboard/baza) = NEUTRALNE kafelki (domyślny szary `--c`), ten sam styl co
  // kolorowe obszary. Trasa bieżąca podświetlana CSS-em przez `aria-current` (NavLink w Radix
  // Slot Tooltipa nie przyjmuje funkcji className — patrz `.rail-glass[aria-current]` w index.css).

  const panelClass =
    'fixed z-[10000] max-h-[80vh] w-60 overflow-y-auto rounded-xl border border-border-secondary bg-bg-elevated p-1.5 shadow-xl';
  const panelHeadClass =
    'px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted';

  return (
    <aside
      ref={railRef}
      data-testid="tenant-sidebar-rail"
      className="relative z-[45] m-3 flex w-16 shrink-0 flex-col items-center rounded-2xl border border-border-secondary bg-bg-elevated py-3 shadow-lg [--rail-gap:11px]"
      style={{ height: 'calc(100vh - 1.5rem)' }}
    >
      {/* Znak marki — OKROJONE logo Workflow365 (sam znak, bez wordmarku); KLIKNIĘCIE otwiera
          „Mapę obszarów" (ten sam drawer co w trybie Mapy dostępu). Kontener nieco szerszy niż
          znak (overflow-hidden ucina dopiero wordmark), img w naturalnej proporcji — cały znak
          widoczny, bez ucięcia. Decyzja Eugenii 2026-09-16. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('tenant.sidebar.accessMap.title')}
        aria-haspopup="menu"
        onClick={() => setMapOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setMapOpen(true);
          }
        }}
        className="mb-3 flex h-10 w-10 cursor-pointer items-center justify-start overflow-hidden rounded-xl transition-colors"
      >
        <img
          src={workflow365Logo}
          alt="Workflow365"
          className="h-10 w-auto max-w-none object-left"
        />
      </div>

      <Tooltip content={t('tenant.sidebar.dashboard')} placement="right">
        <NavLink to="/dashboard" aria-label={t('tenant.sidebar.dashboard')} className="rail-tile">
          <LayoutDashboard />
        </NavLink>
      </Tooltip>

      {/* Kalendarz — stała ikona pod Dashboardem (decyzja Eugenii). */}
      <Tooltip content={t('tenant.calendar.title')} placement="right">
        <NavLink to="/calendar" aria-label={t('tenant.calendar.title')} className="rail-tile mt-2">
          <Calendar />
        </NavLink>
      </Tooltip>

      {/* Ikony OBSZARÓW — wyśrodkowane pionowo; KOLOROWE („czadowe", decyzja Eugenii 2026-09-16):
          każdy obszar dostaje żywy kolor z palety. Aktywny (panel otwarty) i tak ma niebieski tint tła. */}
      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        {railEntries.map((entry, i) => {
          const AreaIcon = resolveIcon(entry.area.icon, Package);
          const colorVar = RAIL_ICON_COLORS[i % RAIL_ICON_COLORS.length];
          return (
            <RailIconButton
              key={entry.area.code}
              active={p1?.key === entry.area.code}
              title={entry.area.name}
              colorVar={colorVar}
              onActivate={(e) => openArea(e, entry)}
              onHover={(e) => openArea(e, entry)}
              onHoverEnd={scheduleClose}
              testId={`rail-area-${entry.area.code}`}
            >
              <AreaIcon />
            </RailIconButton>
          );
        })}
      </div>

      {/* Baza stała (Użytkownicy/Firmy/Ustawienia) + ustawienia menu — na dole. */}
      <div className="flex flex-col items-center gap-2">
        {pinned.map((def) => {
          const Icon = resolveIcon(def.iconName, Package);
          const label = t(def.labelI18nKey, def.defaultLabel);
          return (
            <Tooltip key={def.itemKey} content={label} placement="right">
              <NavLink to={def.path} aria-label={label} className="rail-tile">
                <Icon />
              </NavLink>
            </Tooltip>
          );
        })}
        <MenuModeSwitcher size="md" />
      </div>

      {/* Warstwa ZACIEMNIAJĄCA (backdrop jak przy modalu) — przy otwartej kaskadzie przyciemnia
          CAŁĄ resztę apki (inset-0), żeby menu było wyeksponowane (decyzja Eugenii 2026-09-16).
          Pasek (z-[45]) i panele (z-[10000]) są NAD scrimem (z-40), więc zostają jasne, a reszta
          — także lewa część treści — jest zakryta. Klik/najechanie → zamyka kaskadę. */}
      {p1 &&
        createPortal(
          <div
            // eslint-disable-next-line local/no-raw-overlay -- zaciemnienie reszty ekranu pod kaskadą menu paska (zamyka się najechaniem/kliknięciem), nie okno modalne: bez Esc, fokusu i blokady przewijania
            className="fixed inset-0 z-40 bg-slate-900/30 transition-opacity"
            onMouseEnter={scheduleClose}
            onClick={closeAll}
            aria-hidden="true"
            data-testid="rail-scrim"
          />,
          document.body,
        )}

      {/* Panel obszaru (p1). */}
      {p1 &&
        createPortal(
          <div
            ref={p1Ref}
            role="menu"
            data-testid="rail-panel-area"
            className={panelClass}
            style={{ top: p1.top, left: p1.left }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className={panelHeadClass}>{p1.label}</div>
            {p1.modules.length === 1 ? (
              <div className="space-y-0.5" onClick={closeAll}>
                {p1.modules[0]!.renderItems.map((item, i) => (
                  <InstalledMenuItemNode key={`${item.kind}:${item.slug}:${i}`} item={item} />
                ))}
              </div>
            ) : (
              <div className="space-y-0.5">
                {p1.modules.map(({ mod, renderItems }) => {
                  const leaf = singleNavLeaf(renderItems);
                  if (leaf) {
                    return (
                      <div key={mod.code} onClick={closeAll}>
                        <InstalledMenuItemNode item={leaf} />
                      </div>
                    );
                  }
                  const key = `m:${p1.area.code}:${mod.code}`;
                  const ModIcon = resolveIcon(mod.icon, Package);
                  return (
                    <div
                      key={mod.code}
                      role="menuitem"
                      tabIndex={0}
                      aria-haspopup="menu"
                      onMouseEnter={(e) => openModule(e, key, mod.name, renderItems)}
                      onFocus={(e) => openModule(e, key, mod.name, renderItems)}
                      onClick={(e) => openModule(e, key, mod.name, renderItems)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openModule(e, key, mod.name, renderItems);
                        }
                      }}
                      className={cn(
                        'flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors',
                        p2?.key === key
                          ? 'bg-accent-50 text-accent-700'
                          : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
                      )}
                      data-testid={`rail-module-${p1.area.code}-${mod.code}`}
                    >
                      <ModIcon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate">{mod.name}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 opacity-45" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>,
          document.body,
        )}

      {/* Panel modułu (p2) — kartoteki. */}
      {p2 &&
        createPortal(
          <div
            ref={p2Ref}
            role="menu"
            data-testid="rail-panel-module"
            className={panelClass}
            style={{ top: p2.top, left: p2.left }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onClick={closeAll}
          >
            <div className={panelHeadClass}>{p2.label}</div>
            <div className="space-y-0.5">
              {p2.items.map((item, i) => (
                <InstalledMenuItemNode key={`${item.kind}:${item.slug}:${i}`} item={item} />
              ))}
            </div>
          </div>,
          document.body,
        )}

      {/* „Mapa obszarów" wywoływana kliknięciem logo — ten sam drawer co w trybie Mapy dostępu. */}
      <AreasMapDrawer areas={areas} open={mapOpen} onClose={() => setMapOpen(false)} />
    </aside>
  );
}

/**
 * Tryb menu nr 3 — „MAPA DOSTĘPU" (decyzja Eugenii 2026-09-16). Slim pasek BEZ ikon obszarów
 * (logo-trigger + kalendarz + ustawienia + ustawienia menu); KLIKNIĘCIE logo wysuwa duży drawer
 * „Mapa obszarów" (`AreasMapDrawer`) z pełnym spisem kartotek. Drawer zamyka scrim/klik poza/Esc.
 */
function TenantMenuAccessRail({ areas }: { areas: InstalledMenuArea[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const railRef = useRef<HTMLElement>(null);

  return (
    <aside
      ref={railRef}
      data-testid="tenant-sidebar-access"
      className="relative z-[45] m-3 flex w-16 shrink-0 flex-col items-center rounded-2xl border border-border-secondary bg-bg-elevated py-3 shadow-lg [--rail-gap:11px]"
      style={{ height: 'calc(100vh - 1.5rem)' }}
    >
      {/* LOGO = trigger „Mapy obszarów" — KLIKNIĘCIE logo wysuwa mapę (decyzja Eugenii; osobny
          kafelek-trigger usunięty). Pasek MINIMALNY: Dashboard i baza żyją na mapie. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('tenant.sidebar.menuMode.columns')}
        aria-haspopup="menu"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'mb-3 flex h-10 w-10 cursor-pointer items-center justify-start overflow-hidden rounded-xl transition-colors',
          open && 'bg-accent-50',
        )}
        data-testid="access-drawer-trigger"
      >
        <img
          src={workflow365Logo}
          alt="Workflow365"
          className="h-10 w-auto max-w-none object-left"
        />
      </div>

      {/* Kalendarz — stała ikona na pasku mapy (decyzja Eugenii); dubluje pozycję „Kalendarz"
          w kolumnie „Konto", ale ma być zawsze widoczna wprost na pasku. */}
      <Tooltip content={t('tenant.calendar.title')} placement="right">
        <NavLink to="/calendar" aria-label={t('tenant.calendar.title')} className="rail-tile mt-2">
          <Calendar />
        </NavLink>
      </Tooltip>

      <div className="flex-1" />

      {/* Ustawienia (aplikacji) — na dole paska mapy (decyzja Eugenii). */}
      <Tooltip content={t('tenant.sidebar.settings')} placement="right">
        <NavLink
          to="/settings"
          aria-label={t('tenant.sidebar.settings')}
          className="rail-tile mb-2"
        >
          <Settings />
        </NavLink>
      </Tooltip>

      {/* Ustawienia menu — na samym dole. */}
      <MenuModeSwitcher size="md" />

      <AreasMapDrawer areas={areas} open={open} onClose={() => setOpen(false)} />
    </aside>
  );
}

/**
 * Drawer „MAPA OBSZARÓW" — wysuwany overlay z PEŁNYM spisem wszystkich kartotek, pogrupowanych
 * po obszarach (kolor per obszar z palety logo), konsekwentnie do poziomu kartotek
 * (`flattenAreaLeaves`). Otwierany KLIKNIĘCIEM triggera (logo) w pasku, zamykany przez klik w
 * scrim / klik poza drawerem / Escape (bez hover-close). Kotwiczony do `anchorRef` (pasek).
 * Data-driven z GET /api/my/installed-modules. Wspólny dla trybu „Mapa obszarów"
 * (`TenantMenuAccessRail`) i „Pasek obszarów" (`TenantMenuRail`).
 */
function AreasMapDrawer({
  areas,
  open,
  onClose,
  variant = 'modal',
}: {
  areas: InstalledMenuArea[];
  open: boolean;
  onClose: () => void;
  /** `modal` — overlay na środku ekranu (paski obszarów). `page` — zwykły widok w obszarze treści. */
  variant?: 'modal' | 'page';
}) {
  const { t } = useTranslation();
  const customModulesQuery = useCustomModulesForMenu();
  // Mapa = pełny katalog (niezależny od menu); do czasu odpowiedzi — wykupione obszary z paska.
  const areaMapQuery = useAreaMapQuery({ enabled: variant === 'page' || open });
  const mapAreas = areaMapQuery.data ?? areas;

  // Kolumna „Konto" (neutralna, bez ikon) — baza platformowa zdjęta z paska na mapę:
  // Dashboard + Użytkownicy/Firmy/Ustawienia. Kolejność = Dashboard, potem baza stała.
  const accountItems: { to: string; label: string }[] = [
    { to: '/dashboard', label: t('tenant.sidebar.dashboard') },
    { to: '/calendar', label: t('tenant.calendar.title') },
    ...SYSTEM_ITEMS.filter((s) => PINNED_BASE_ITEM_KEYS.includes(s.itemKey)).map((s) => ({
      to: s.path,
      label: t(s.labelI18nKey, s.defaultLabel),
    })),
  ];

  // Kolumny mapy: każdy obszar → MODUŁY → spłaszczone kartoteki (podział na moduły, np. HR:
  // BHP i badania / Urlopy / …). Obszar jednomodułowy renderuje kartoteki płasko (bez podnagłówka).
  // Kolor per obszar. Bazę (Dashboard/Użytkownicy/Firmy/Ustawienia) odsiewamy — żyje w kolumnie
  // „Konto", nie dublujemy. Obszary/moduły bez kartotek pomijane.
  // 7 klas dokumentów to pakiet BAZOWY (crm_core, ADR-0083) — po wycofaniu modułów doc_* nie
  // ma ich w liściach zainstalowanych obszarów, więc dokładamy je do obszaru „Systemowe" jako
  // pod-grupę „Dokumenty" (niezależnie od customowych menu tenanta). Tylko gdy serwer ZWRÓCIŁ
  // obszar „Systemowe" (= aktywna subskrypcja pakietu bazowego) — bez niego linki dałyby 402,
  // a w trakcie ładowania/błędu zapytania mapa nie udaje pełnego spisu.
  const documentsGroup: AccessMapModule = {
    code: ACCESS_MAP_DOCUMENTS_GROUP,
    name: t('tenant.sidebar.accessMap.documents'),
    leaves: DOCUMENT_KARTOTEKI.map((d) => ({
      to: d.path,
      label: t(`tenant.tabs.titles.${DOCUMENT_TAB_TITLE_KEYS[d.slug]}`, d.label),
    })),
  };
  const columns = mapAreas
    .map((area, i) => {
      const modules: AccessMapModule[] = groupAreaItemsByModule(area).map((m) => ({
        name: m.name,
        code: m.code,
        leaves: flattenAreaLeaves(m.items, true)
          .map((leaf) => ({
            to: installedLeafPath(leaf) ?? '#',
            label: leaf.name,
            inactive: leaf.inactive === true,
          }))
          .filter((leaf) => !ACCESS_MAP_BASE_PATHS.has(leaf.to.split('?')[0]!)),
      }));
      if (area.code === SYSTEM_AREA_CODE) {
        const present = new Set(modules.flatMap((m) => m.leaves.map((l) => l.to.split('?')[0])));
        modules.push({
          ...documentsGroup,
          leaves: documentsGroup.leaves.filter((l) => !present.has(l.to)),
        });
      }
      const nonEmpty = modules.filter((m) => m.leaves.length > 0);
      const total = nonEmpty.reduce((n, m) => n + m.leaves.length, 0);
      return {
        area,
        colorVar: RAIL_ICON_COLORS[i % RAIL_ICON_COLORS.length]!,
        modules: nonEmpty,
        total,
      };
    })
    .filter((c) => c.total > 0);

  // Kolumna „Customowe" = kartoteki WŁASNE klienta (zbudowane ręcznie, bez szablonu) z ich listy —
  // mapa nie zależy od menu (decyzja 2026-10-05). Kartoteki z modułów-szablonów są w swoich
  // obszarach.
  const customLeaves: AccessMapLeaf[] = (customModulesQuery.data ?? [])
    .filter((m) => m.sourceTemplateModuleId === null)
    .map((m) => ({ to: `/modules/${m.slug}`, label: m.name }));
  const customColorVar = RAIL_ICON_COLORS[columns.length % RAIL_ICON_COLORS.length]!;

  // Siatka: Konto + obszary + (Customowe, gdy są customowe menu).
  const gridCount = 1 + columns.length + (customLeaves.length > 0 ? 1 : 0);

  if (variant === 'modal' && !open) return null;

  const body = (
    <>
      <div className="mb-4 pr-10">
        <div className="text-base font-semibold text-text-primary">
          {t('tenant.sidebar.accessMap.title')}
        </div>
        <div className="text-sm text-text-secondary">{t('tenant.sidebar.accessMap.subtitle')}</div>
      </div>
      {areaMapQuery.isError && (
        <Alert tone="warning" className="mb-4" data-testid="area-map-error">
          {t('tenant.sidebar.accessMap.loadError')}
        </Alert>
      )}
      {/* Obszary obok siebie w kolumnach o stałej szerokości, wyrównane do góry — brak
                zawijania (drawer szeroki na tyle, ile kolumn; scroll poziomy przy braku miejsca). */}
      <div
        className="grid items-start gap-3"
        style={{ gridTemplateColumns: `repeat(${gridCount}, 13rem)` }}
      >
        {/* Kolumna „Konto" — neutralny SEGMENT z lekkim tłem (baza platformowa). */}
        <div className="w-52 shrink-0 rounded-xl bg-bg-secondary p-3">
          <div className="mb-2.5 flex items-center gap-2 border-b border-border-secondary pb-2">
            <span className="truncate text-sm font-bold text-text-primary">
              {t('tenant.sidebar.accessMap.account')}
            </span>
            <span className="ml-auto text-[11px] font-bold text-text-muted">
              {accountItems.length}
            </span>
          </div>
          <div className="space-y-0.5" onClick={onClose}>
            {accountItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className="block truncate rounded-lg px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-black/[0.05] hover:text-text-primary"
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>

        {columns.map(({ area, colorVar, modules, total }) => {
          const AreaIcon = resolveIcon(area.icon, Package);
          const color = `rgb(${colorVar})`;
          // Kartoteka = sama nazwa (bez ikony — ikony liści zaśmiecały widok, decyzja Eugenii).
          const leafLink = (leaf: AccessMapLeaf, i: number) =>
            leaf.inactive ? (
              <div
                key={`${leaf.label}:${i}`}
                aria-disabled="true"
                title={t('tenant.sidebar.accessMap.notPurchasedHint')}
                className="flex cursor-not-allowed items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-text-muted"
              >
                <span className="truncate">{leaf.label}</span>
                <span className="ml-auto shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {t('tenant.sidebar.accessMap.notPurchased')}
                </span>
              </div>
            ) : (
              <NavLink
                key={`${leaf.to}:${i}`}
                to={leaf.to}
                className="block truncate rounded-lg px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-black/[0.05] hover:text-text-primary"
              >
                {leaf.label}
              </NavLink>
            );
          const single = modules.length === 1;
          return (
            <div
              key={area.code}
              className="w-52 shrink-0 rounded-xl p-3"
              style={{ backgroundColor: `rgb(${colorVar} / 0.05)` }}
            >
              {/* Nagłówek A: ikona KONTUROWA w kolorze + nazwa w kolorze obszaru (bez wypełnionego kafelka). */}
              <div
                className="mb-2.5 flex items-center gap-2 border-b pb-2"
                style={{ borderColor: `rgb(${colorVar} / 0.18)` }}
              >
                <span className="shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]" style={{ color }}>
                  <AreaIcon />
                </span>
                <span className="truncate text-sm font-bold" style={{ color }}>
                  {area.name}
                </span>
                <span
                  className="ml-auto text-[11px] font-bold"
                  style={{ color: `rgb(${colorVar} / 0.75)` }}
                >
                  {total}
                </span>
              </div>
              <div className="space-y-3" onClick={onClose}>
                {single ? (
                  <div className="space-y-0.5">{modules[0]!.leaves.map(leafLink)}</div>
                ) : (
                  modules.map((mod) => (
                    <div key={mod.code}>
                      {/* Nagłówek MODUŁU — wersaliki w barwie obszaru (bez kropki; decyzja
                                Eugenii). HR → BHP i badania / Urlopy / … */}
                      <div
                        className="mb-1 mt-0.5 px-2 text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: `rgb(${colorVar} / 0.85)` }}
                      >
                        {mod.name}
                      </div>
                      <div className="space-y-0.5">{mod.leaves.map(leafLink)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}

        {/* Kolumna „Customowe" — kartoteki własne klienta (z ich listy, nie z menu). */}
        {customLeaves.length > 0 && (
          <div
            className="w-52 shrink-0 rounded-xl p-3"
            style={{ backgroundColor: `rgb(${customColorVar} / 0.05)` }}
          >
            <div
              className="mb-2.5 flex items-center gap-2 border-b pb-2"
              style={{ borderColor: `rgb(${customColorVar} / 0.18)` }}
            >
              <span
                className="shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]"
                style={{ color: `rgb(${customColorVar})` }}
              >
                <Boxes />
              </span>
              <span
                className="truncate text-sm font-bold"
                style={{ color: `rgb(${customColorVar})` }}
              >
                {t('tenant.sidebar.accessMap.customModules')}
              </span>
              <span
                className="ml-auto text-[11px] font-bold"
                style={{ color: `rgb(${customColorVar} / 0.75)` }}
              >
                {customLeaves.length}
              </span>
            </div>
            <div className="space-y-0.5" onClick={onClose}>
              {customLeaves.map((lf, i) => (
                <NavLink
                  key={`${lf.to}:${i}`}
                  to={lf.to}
                  className="block truncate rounded-lg px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-black/[0.05] hover:text-text-primary"
                >
                  {lf.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Wariant „page" — mapa jako zwykły widok w obszarze treści. Treść w KARCIE (biała
  // `surface-card` + border + radius + Elevation/Subtle), jak karta `ConfigurableTable`.
  if (variant === 'page') {
    return (
      <main
        className="h-full min-h-0 overflow-auto bg-bg-secondary p-6"
        data-testid="area-map-page"
      >
        <div className="rounded-xl border border-border-secondary bg-bg-elevated p-5 shadow-[0_1px_2px_rgba(23,23,33,0.06)]">
          {body}
        </div>
      </main>
    );
  }

  // Wariant „modal" — overlay na środku ekranu z krzyżykiem (paski obszarów). Zachowanie (tło, Esc,
  // fokus, blokada przewijania) daje wspólny rdzeń `Overlay`; z-[10000] zostaje, bo okno musi
  // leżeć nad paskiem (z-45) i jego panelami bocznymi (z-10000, wcześniej w DOM).
  return (
    <Overlay
      open={open}
      onClose={onClose}
      ariaLabel={t('tenant.sidebar.accessMap.title')}
      testId="access-scrim"
      panelTestId="access-drawer"
      backdropClassName="z-[10000] flex items-center justify-center bg-slate-900/50 p-6"
      panelClassName="relative max-h-[calc(100vh-3rem)] max-w-[calc(100vw-3rem)] overflow-auto rounded-2xl border border-border-secondary bg-bg-elevated p-5 shadow-xl"
    >
      <IconButton
        onClick={onClose}
        aria-label={t('common.close')}
        data-testid="access-drawer-close"
        className="absolute right-3 top-3 h-8 w-8 text-text-muted"
      >
        <X aria-hidden="true" />
      </IconButton>
      {body}
    </Overlay>
  );
}

/**
 * Strona „Mapa obszarów" — zwykły widok w obszarze treści (trasa /area-map), zamiast modala.
 * Pobiera zainstalowane obszary i renderuje wspólny `AreasMapDrawer` w wariancie `page`.
 */
export function AreaMapPage() {
  const installedQuery = useInstalledModulesMenuQuery();
  return (
    <AreasMapDrawer areas={installedQuery.data ?? []} open onClose={() => {}} variant="page" />
  );
}

/**
 * Liść menu modułu wskazujący kartotekę/ekran z modułu, którego klient nie ma — wyszarzony,
 * bez linku, z plakietką „Nieaktywny" (ten sam wygląd co nieaktywna pozycja własnego menu).
 */
function InactiveMenuLeaf({
  label,
  iconName,
  testId,
}: {
  label: string;
  iconName: string | null;
  testId: string;
}) {
  const { t } = useTranslation();
  const collapsed = useIsSidebarCollapsed();
  const Icon = resolveIcon(iconName, Package);
  return (
    <div
      className={`flex items-center rounded-lg py-2.5 text-sm text-text-muted cursor-not-allowed opacity-70 ${
        collapsed ? 'justify-center px-2' : 'gap-3 px-4'
      }`}
      title={
        collapsed
          ? `${label} — ${t('tenant.sidebar.inactiveBadge')}`
          : t('tenant.sidebar.moduleInactiveTooltip')
      }
      data-testid={`sidebar-installed-inactive-${testId}`}
      data-state="inactive"
      aria-disabled="true"
    >
      <SidebarIcon icon={Icon} muted />
      <SidebarItemLabel>{label}</SidebarItemLabel>
      {!collapsed && (
        <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          {t('tenant.sidebar.inactiveBadge')}
        </span>
      )}
    </div>
  );
}

/**
 * Rekurencyjny render pojedynczej pozycji obszaru. Liść moduł/rodzaj danych → link do
 * `/modules/{slug}` (moduł systemowy może mieć special-case landing). Węzły fragmentu
 * domyślnego menu modułu (kreator/hub): sekcja → nagłówek + dzieci, custom link →
 * link (wew./zew.), separator → kreska.
 */
function InstalledMenuItemNode({
  item,
  color,
}: {
  item: InstalledMenuItem;
  /** Kolor obszaru modułu (per obszar); brak → kolor po nazwie ikony. */
  color?: string | null;
}) {
  const collapsed = useIsSidebarCollapsed();
  const Icon = resolveIcon(item.icon, Package);
  const navClass = ({ isActive }: { isActive: boolean }) =>
    tenantSidebarItemClass(collapsed, isActive);

  if (item.kind === 'separator') {
    return <hr className="my-2 border-border-secondary" />;
  }

  if (item.kind === 'section') {
    // Nagłówek sekcji ukryty przy zwiniętym sidebarze; dzieci (ikony) zostają.
    return (
      <div className="mt-2">
        {!collapsed && <SidebarSectionHeader label={item.name} iconName={item.icon} />}
        {item.children?.map((child) => (
          <InstalledMenuItemNode key={child.slug} item={child} color={color} />
        ))}
      </div>
    );
  }

  if (item.inactive) {
    return <InactiveMenuLeaf label={item.name} iconName={item.icon} testId={item.slug} />;
  }

  if (item.kind === 'link') {
    const href = item.url ?? '#';
    const isExternal = item.external || /^https?:\/\//.test(href);
    if (isExternal) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? item.name : undefined}
          className={navClass({ isActive: false })}
        >
          <SidebarIcon icon={Icon} color={color} iconName={item.icon} sizeClass="h-5 w-5" />
          <SidebarItemLabel>{item.name}</SidebarItemLabel>
        </a>
      );
    }
    return (
      <NavLink to={href} title={collapsed ? item.name : undefined} className={navClass}>
        <SidebarIcon icon={Icon} color={color} iconName={item.icon} sizeClass="h-5 w-5" />
        <SidebarItemLabel>{item.name}</SidebarItemLabel>
      </NavLink>
    );
  }

  // kind 'system' | 'template' — link do modułu/rodzaju danych. Jedno źródło prawdy
  // ścieżki: `installedLeafPath` (T20.3 special-case landing dla modułów systemowych,
  // np. processes → /processes/decisions/pending, ORAZ dopisanie `?documentTypeIds=`
  // dla kartotek klasy dokumentu, np. Teczka → DOKPR) — bez lokalnej duplikacji logiki.
  // Pozycja z przypisanym widokiem (układ formularza/listy z menu modułu) — `?mi=` każe stronie
  // kartoteki otworzyć się w tym układzie (jak w menu własnym klienta); dopisuje je
  // `installedItemPath` (tę samą ścieżkę liczą mapy ikon/etykiet zakładek).
  const landingPath = installedItemPath(item) ?? '#';
  return (
    <NavLink
      to={landingPath}
      onClick={
        item.menuItemId ? (e) => announceMenuItemActivated(item.menuItemId as string, e) : undefined
      }
      data-testid={`sidebar-installed-module-${item.slug}`}
      title={collapsed ? item.name : undefined}
      className={navClass}
    >
      <SidebarIcon icon={Icon} color={color} iconName={item.icon} sizeClass="h-5 w-5" />
      <SidebarItemLabel>{item.name}</SidebarItemLabel>
      {/* Badge liczby oczekujących decyzji przy pozycji „Decyzje" (moduł processes).
          W modelu „widok = moduł" pozycja żyje w tym węźle, nie w stopce — badge
          renderujemy tu, analogicznie do przypiętego wariantu i CustomLayoutNav. */}
      {item.kind === 'system' && item.slug === 'processes' && !collapsed && (
        <ProcessesPendingBadge />
      )}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// ADR-0031 — Custom layout renderer (gdy admin tenanta zapisał hierarchy).
// Renderuje drzewo MenuItem rekurencyjnie. Module lookup z `useMyModules` żeby
// rozróżnić aktywne (NavLink) vs nieaktywne (greyed + tooltip).
//
// Hub Kreatora Krok 4: renderer sam montuje zapytania o moduły custom i obszary
// zainstalowanych modułów (w trybie własnego układu sekcje fallbacku, które je
// montowały, nie startują). Element `module` wskazujący slug modułu custom jest
// aktywnym linkiem (bugfix „nieaktywnych"), element `installed_area` renderuje
// sekcję obszaru z rodzajami danych.
// ---------------------------------------------------------------------------

function CustomLayoutNav({
  hierarchy,
  modules,
}: {
  hierarchy: MenuItem[];
  modules: MyModulesResponse;
}) {
  const customModulesQuery = useCustomModulesForMenu();
  const installedAreasQuery = useInstalledModulesMenuQuery();

  const subByCode = new Map(modules.subscriptions.map((s) => [s.code, s]));
  const catByCode = new Map(modules.catalog.map((c) => [c.code, c]));
  const systemByKey = new Map(SYSTEM_ITEMS.map((s) => [s.itemKey, s]));
  // Silent fail (jak sekcje fallbacku): błąd zapytania → puste mapy → element
  // custom zostaje „nieaktywny", obszar nie renderuje się.
  const customBySlug = new Map((customModulesQuery.data ?? []).map((m) => [m.slug, m]));
  const areaByCode = new Map((installedAreasQuery.data ?? []).map((a) => [a.code, a]));
  // Kreator menu tenanta (2026-07-02): pozycje `module` mogą wskazywać kartotekę
  // z palety po slugu (też SYSTEMOWĄ, np. 'employees'). Płaska mapa liści drzewa
  // zainstalowanych obszarów rozwiązuje je na żywo — moduł wygasł → pozycja
  // nieaktywna (decyzja D2), bez kasowania układu klienta.
  const installedLeafBySlug = new Map<string, InstalledMenuItem>();
  {
    const walk = (items: InstalledMenuItem[]) => {
      for (const it of items) {
        if (it.kind === 'section') {
          walk(it.children ?? []);
          continue;
        }
        // Liść nieaktywny (cel z niewykupionego modułu) nie może „aktywować" pozycji
        // własnego menu klienta wskazującej ten sam slug.
        if (
          (it.kind === 'system' || it.kind === 'template') &&
          !it.inactive &&
          !installedLeafBySlug.has(it.slug)
        ) {
          installedLeafBySlug.set(it.slug, it);
        }
      }
    };
    for (const a of installedAreasQuery.data ?? []) walk(a.items);
  }

  return (
    <>
      {hierarchy.map((item) => (
        <RenderMenuItem
          key={item.id}
          item={item}
          subByCode={subByCode}
          catByCode={catByCode}
          systemByKey={systemByKey}
          customBySlug={customBySlug}
          areaByCode={areaByCode}
          installedLeafBySlug={installedLeafBySlug}
        />
      ))}
    </>
  );
}

function RenderMenuItem({
  item,
  subByCode,
  catByCode,
  systemByKey,
  customBySlug,
  areaByCode,
  installedLeafBySlug,
}: {
  item: MenuItem;
  subByCode: Map<string, MyModulesResponse['subscriptions'][0]>;
  catByCode: Map<string, CatalogModule>;
  systemByKey: Map<SystemItemKey, (typeof SYSTEM_ITEMS)[number]>;
  customBySlug: Map<string, CustomModuleSidebarItem>;
  areaByCode: Map<string, InstalledMenuArea>;
  installedLeafBySlug: Map<string, InstalledMenuItem>;
}) {
  const { t } = useTranslation();
  const collapsed = useIsSidebarCollapsed();

  if (item.type === 'separator') {
    return <hr className="my-2 border-border-secondary" />;
  }

  if (item.type === 'section') {
    return (
      <div className="mt-3">
        {!collapsed && <SidebarSectionHeader label={item.label ?? '—'} iconName={item.iconName} />}
        {item.children?.map((child) => (
          <RenderMenuItem
            key={child.id}
            item={child}
            subByCode={subByCode}
            catByCode={catByCode}
            systemByKey={systemByKey}
            customBySlug={customBySlug}
            areaByCode={areaByCode}
            installedLeafBySlug={installedLeafBySlug}
          />
        ))}
      </div>
    );
  }

  if (item.type === 'installed_area') {
    const area = item.areaCode ? areaByCode.get(item.areaCode) : undefined;
    // Obszar nieznany / bez opłaconych modułów / subskrypcja wygasła → silent
    // (spójne z sekcją obszarów fallbacku — backend gate'uje po subskrypcji).
    if (!area || area.items.length === 0) return null;
    return (
      <div className="mt-3" data-testid={`sidebar-installed-area-${area.code}`}>
        {!collapsed && (
          <div className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            {item.label ?? area.name}
          </div>
        )}
        {area.items.map((entry) => (
          <InstalledMenuItemNode key={entry.slug} item={entry} color={area.color} />
        ))}
      </div>
    );
  }

  if (item.type === 'module') {
    const code = item.moduleCode ?? '';
    const cat = catByCode.get(code);
    const custom = customBySlug.get(code);
    // Kartoteka z palety kreatora menu (wstawiana po slugu — też systemowa,
    // np. 'employees'): rozwiązana na żywo z liści zainstalowanych obszarów.
    const installedLeaf = installedLeafBySlug.get(code);
    // Kartoteka dokumentu (Faktury/Umowy/Pisma…) = pakiet BAZOWY (crm_core), nie moduł
    // (ADR-0083). Aktywna gdy tenant ma moduł-właściciel kartoteki — niezależnie od tego, że
    // nie ma jej już w liściach zainstalowanych obszarów (doc_* wycofane). Właścicielem jest
    // crm_core, poza Magazynami (moduł `warehouses`): bez niego pozycja jest „Nieaktywna"
    // zamiast prowadzić do ekranu z błędem ładowania rekordów.
    const baseKartoteka = BASE_PACKAGE_KARTOTEKA_SLUGS.has(code)
      ? BASE_KARTOTEKA_BY_SLUG.get(code)
      : undefined;
    const isBaseDocKartoteka =
      baseKartoteka !== undefined &&
      subByCode.has(findSystemKartotekaTarget(code)?.ownerModuleCode ?? 'crm_core');
    // Aktywny = subskrypcja katalogowa LUB moduł custom tenanta (bugfix huba:
    // moduły custom nie mają subskrypcji) LUB widoczna kartoteka z obszarów LUB
    // kartoteka dokumentu z pakietu bazowego.
    const isActive =
      subByCode.has(code) ||
      custom !== undefined ||
      installedLeaf !== undefined ||
      isBaseDocKartoteka;
    // Kartoteka pakietu bazowego bez wykupionego modułu-właściciela (Magazyny bez modułu
    // `warehouses`) — UKRYTA, nie wyszarzona: tenant nie ma czego tu odblokować w ramach
    // swojego pakietu, a paleta Kreatora też jej nie proponuje (decyzja Eugenii 2026-10-21).
    if (!isActive && baseKartoteka !== undefined) return null;
    const Icon = resolveIcon(
      item.iconName ?? cat?.icon ?? custom?.icon ?? installedLeaf?.icon ?? baseKartoteka?.iconName,
      Package,
    );
    const label =
      item.label ??
      cat?.name ??
      custom?.name ??
      installedLeaf?.name ??
      baseKartoteka?.label ??
      code;

    if (!isActive) {
      return (
        <div
          className={`flex items-center rounded-lg py-2.5 text-sm text-text-muted cursor-not-allowed opacity-70 ${
            collapsed ? 'justify-center px-2' : 'gap-3 px-4'
          }`}
          title={
            collapsed
              ? `${label} — ${t('tenant.sidebar.inactiveBadge')}`
              : t('tenant.sidebar.moduleInactiveTooltip')
          }
          data-testid={`sidebar-custom-module-${code}`}
          data-state="inactive"
          aria-disabled="true"
        >
          <SidebarIcon icon={Icon} muted />
          <SidebarItemLabel>{label}</SidebarItemLabel>
          {!collapsed && (
            <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {t('tenant.sidebar.inactiveBadge')}
            </span>
          )}
        </div>
      );
    }

    // Pozycja z przypisanym widokiem (layout listy/formularza lub filtry stałe) — doklej
    // `?mi=<id>` żeby strona klienta honorowała to przypisanie. Dotyczy RÓWNIEŻ kartotek
    // systemowych z dedykowanymi stronami (`installedLeafPath`, np. Teczka →
    // /internal-docs?documentTypeIds=...) — filtry stałe z Kreatora muszą działać
    // niezależnie od tego, czy cel to wspólna strona modułu czy dedykowana trasa;
    // `baseTarget` może już nieść `?documentTypeIds=`, więc doklejamy `&` albo `?`.
    const hasViewAssignment = menuItemHasViewAssignment(item);
    // Kartoteka dokumentu z pakietu BAZOWEGO (§ BASE_PACKAGE_KARTOTEKA_SLUGS) to WŁASNA
    // pozycja tenanta po slugu — `installedLeafBySlug` bywa rozwiązywana na liść
    // ZAINSTALOWANEGO modułu o tym samym slugu (np. 'specjalne' → Urlopy/WU), który niesie
    // zawężenie do TAMTEGO modułu (documentTypeId/documentTypeIds). Taką narrowację wolno
    // dziedziczyć tylko instalowanym liściom obszarów (`InstalledMenuItemNode`), NIE własnej
    // pozycji tenanta wskazującej bazową kartotekę — inaczej własna pozycja „Dokumenty
    // specjalne" cicho zawężałaby się do typu dokumentu innego modułu. Zawężenie własnej
    // pozycji może przyjść WYŁĄCZNIE z jej własnego `view.baseFilters` (obsłużone niżej przez
    // `?mi=`), więc dla bazowej kartoteki bierzemy zawsze `baseKartoteka.path` bez narrowingu
    // z `installedLeaf` — ten zostaje użyty tylko do ikony/etykiety/stanu aktywności wyżej.
    const baseTarget = baseKartoteka
      ? baseKartoteka.path
      : ((installedLeaf && installedLeafPath(installedLeaf)) ?? `/modules/${code}`);
    const target = hasViewAssignment
      ? `${baseTarget}${baseTarget.includes('?') ? '&' : '?'}mi=${encodeURIComponent(item.id)}`
      : baseTarget;
    return (
      <NavLink
        to={target}
        onClick={hasViewAssignment ? (e) => announceMenuItemActivated(item.id, e) : undefined}
        data-testid={`sidebar-custom-module-${code}`}
        data-state="active"
        title={collapsed ? label : undefined}
        className={({ isActive: linkActive }) => sidebarItemClass(collapsed, linkActive)}
      >
        <SidebarIcon
          icon={Icon}
          iconName={item.iconName ?? cat?.icon ?? custom?.icon ?? installedLeaf?.icon}
        />
        <SidebarItemLabel>{label}</SidebarItemLabel>
        {code === 'processes' && !collapsed && <ProcessesPendingBadge />}
      </NavLink>
    );
  }

  if (item.type === 'system_item') {
    const def = item.itemKey ? systemByKey.get(item.itemKey) : undefined;
    if (!def) return null;
    const Icon = resolveIcon(item.iconName ?? def.iconName, Package);
    const label = item.label ?? t(def.labelI18nKey, def.defaultLabel);
    return (
      <NavLink
        to={def.path}
        data-testid={`sidebar-system-item-${def.itemKey}`}
        title={collapsed ? label : undefined}
        className={({ isActive }) => sidebarItemClass(collapsed, isActive)}
      >
        <SidebarIcon icon={Icon} iconName={item.iconName ?? def.iconName} />
        <SidebarItemLabel>{label}</SidebarItemLabel>
      </NavLink>
    );
  }

  if (item.type === 'custom_link') {
    const url = item.url ?? '#';
    const label = item.label ?? url;
    const Icon = resolveIcon(item.iconName, Package);
    // Defense in depth — backend walidator blokuje javascript:/data:/vbscript: przy PUT.
    // Renderer sprawdza ponownie na wypadek gdyby URL trafił do bazy z pominięciem API
    // (ręczny INSERT, import z backupu, future restore z JSON). Bez tego XSS przez href.
    const urlKind = classifyMenuLinkUrl(url);
    const isSafeRelative = urlKind === 'relative';
    const isSafeAbsolute = urlKind === 'absolute';
    const className = `flex items-center rounded-lg py-2.5 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary ${
      collapsed ? 'justify-center px-2' : 'gap-3 px-4'
    }`;

    if (!isSafeRelative && !isSafeAbsolute) {
      if (import.meta.env.DEV) {
        console.warn(`[Sidebar] custom_link with disallowed URL scheme rejected: ${url}`);
      }
      return (
        <div
          className={`flex items-center rounded-lg py-2.5 text-sm text-text-muted cursor-not-allowed opacity-70 ${
            collapsed ? 'justify-center px-2' : 'gap-3 px-4'
          }`}
          title={collapsed ? label : t('tenant.sidebar.customLinkInvalidUrl')}
          data-testid={`sidebar-custom-link-${item.id}`}
          data-state="invalid"
          aria-disabled="true"
        >
          <SidebarIcon icon={Icon} muted />
          <SidebarItemLabel>{label}</SidebarItemLabel>
        </div>
      );
    }

    // `external=true` honorowane też dla relatywnych URL-i — admin może chcieć
    // otworzyć wewnętrzny route w nowej karcie (np. side-by-side compare).
    // NavLink nie obsługuje target=_blank, więc używamy zwykłego <a>.
    if (isSafeRelative && !item.external) {
      return (
        <NavLink
          to={url}
          data-testid={`sidebar-custom-link-${item.id}`}
          title={collapsed ? label : undefined}
          className={({ isActive }) =>
            [
              className,
              isActive
                ? 'bg-accent-50 text-accent-700 font-medium dark:bg-accent-900/30 dark:text-accent-300'
                : '',
            ].join(' ')
          }
        >
          <SidebarIcon icon={Icon} iconName={item.iconName} />
          <SidebarItemLabel>{label}</SidebarItemLabel>
        </NavLink>
      );
    }

    return (
      <a
        href={url}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
        data-testid={`sidebar-custom-link-${item.id}`}
        title={collapsed ? label : undefined}
        className={className}
      >
        <SidebarIcon icon={Icon} iconName={item.iconName} />
        <SidebarItemLabel>{label}</SidebarItemLabel>
      </a>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// ADR-0034 — Admin custom layout renderer (gdy super_admin zapisał hierarchy
// w /menu-builder admin scope). Analog tenant CustomLayoutNav z różnicami:
//   - BEZ subByCode/catByCode lookups (admin scope nie ma module subscriptions).
//   - System items z ADMIN_SYSTEM_ITEMS (16 items) zamiast tenant SYSTEM_ITEMS.
//   - Brak handling 'module' type (AdminMenuItemType nie zawiera modułów).
//   - XSS guard zachowany dla custom_link (defense in depth analog tenant).
//
// **Świadoma duplikacja** z CustomLayoutNav tenant — wyciągnięcie do shared
// component odłożone — zadanie Boards 1523 (extract CustomLayoutNav
// → dedup ~200 LOC admin/tenant).
// ---------------------------------------------------------------------------

// Module-level lookup — ADMIN_SYSTEM_ITEMS jest immutable const, nie potrzebujemy
// rebuilding Mapy per-render. Stabilna referencja = brak invalidacji memoizacji
// w children (RenderAdminMenuItem).
const ADMIN_SYSTEM_BY_KEY: ReadonlyMap<AdminSystemItemKey, AdminSystemItemDefinition> = new Map(
  ADMIN_SYSTEM_ITEMS.map((s) => [s.itemKey, s]),
);

function AdminCustomLayoutNav({ hierarchy }: { hierarchy: AdminMenuItem[] }) {
  return (
    <>
      {hierarchy.map((item) => (
        <RenderAdminMenuItem key={item.id} item={item} systemByKey={ADMIN_SYSTEM_BY_KEY} />
      ))}
    </>
  );
}

function RenderAdminMenuItem({
  item,
  systemByKey,
  color,
}: {
  item: AdminMenuItem;
  systemByKey: ReadonlyMap<AdminSystemItemKey, AdminSystemItemDefinition>;
  /** Kolor sekcji-rodzica (grupa) — dziedziczony przez pozycje sekcji. */
  color?: string;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const collapsed = useIsSidebarCollapsed();

  if (item.type === 'separator') {
    return <hr className="my-2 border-border-secondary" />;
  }

  if (item.type === 'section') {
    // Kolor grupy: cała sekcja jednym kolorem (nie tęcza per ikona).
    const sectionColor = adminSectionColor(item.label);
    return (
      <div className="mt-3">
        {!collapsed && <SidebarSectionHeader label={item.label ?? '—'} iconName={item.iconName} />}
        {item.children?.map((child) => (
          <RenderAdminMenuItem
            key={child.id}
            item={child}
            systemByKey={systemByKey}
            color={sectionColor}
          />
        ))}
      </div>
    );
  }

  if (item.type === 'system_item') {
    const def = item.itemKey ? systemByKey.get(item.itemKey) : undefined;
    if (!def) return null;
    // Ta sama bramka co w menu statycznym (pozycja dostępna tylko z uprawnieniem).
    if (def.requiredPermission && !hasPermission(def.requiredPermission)) return null;
    const Icon = resolveIcon(item.iconName ?? def.iconName, Package);
    const label = item.label ?? t(def.labelI18nKey, def.defaultLabel);
    return (
      <NavLink
        to={def.path}
        data-testid={`admin-sidebar-system-item-${def.itemKey}`}
        title={collapsed ? label : undefined}
        className={({ isActive }) => sidebarItemClass(collapsed, isActive)}
      >
        <SidebarIcon icon={Icon} color={color} iconName={item.iconName ?? def.iconName} />
        <SidebarItemLabel>{label}</SidebarItemLabel>
      </NavLink>
    );
  }

  if (item.type === 'custom_link') {
    const url = item.url ?? '#';
    const label = item.label ?? url;
    const Icon = resolveIcon(item.iconName, Package);
    // Defense in depth — analog tenant XSS guard (linie 600-665).
    // Backend walidator blokuje javascript:/data:/vbscript: przy PUT/PATCH,
    // renderer sprawdza ponownie na wypadek ręcznego INSERT albo restore z JSON.
    const urlKind = classifyMenuLinkUrl(url);
    const isSafeRelative = urlKind === 'relative';
    const isSafeAbsolute = urlKind === 'absolute';
    const className = `flex items-center rounded-lg py-2.5 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary ${
      collapsed ? 'justify-center px-2' : 'gap-3 px-4'
    }`;

    if (!isSafeRelative && !isSafeAbsolute) {
      if (import.meta.env.DEV) {
        console.warn(`[AdminSidebar] custom_link with disallowed URL scheme rejected: ${url}`);
      }
      return (
        <div
          className={`flex items-center rounded-lg py-2.5 text-sm text-text-muted cursor-not-allowed opacity-70 ${
            collapsed ? 'justify-center px-2' : 'gap-3 px-4'
          }`}
          title={collapsed ? label : t('admin.sidebar.customLinkInvalidUrl')}
          data-testid={`admin-sidebar-custom-link-${item.id}`}
          data-state="invalid"
          aria-disabled="true"
        >
          <SidebarIcon icon={Icon} muted />
          <SidebarItemLabel>{label}</SidebarItemLabel>
        </div>
      );
    }

    if (isSafeRelative && !item.external) {
      return (
        <NavLink
          to={url}
          data-testid={`admin-sidebar-custom-link-${item.id}`}
          title={collapsed ? label : undefined}
          className={({ isActive }) =>
            [
              className,
              isActive
                ? 'bg-accent-50 text-accent-700 font-medium dark:bg-accent-900/30 dark:text-accent-300'
                : '',
            ].join(' ')
          }
        >
          <SidebarIcon icon={Icon} color={color} iconName={item.iconName} />
          <SidebarItemLabel>{label}</SidebarItemLabel>
        </NavLink>
      );
    }

    return (
      <a
        href={url}
        target={item.external ? '_blank' : undefined}
        rel={item.external ? 'noopener noreferrer' : undefined}
        data-testid={`admin-sidebar-custom-link-${item.id}`}
        title={collapsed ? label : undefined}
        className={className}
      >
        <SidebarIcon icon={Icon} color={color} iconName={item.iconName} />
        <SidebarItemLabel>{label}</SidebarItemLabel>
      </a>
    );
  }

  return null;
}
